import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Square, Circle, Save, X, Loader2,
  ZoomIn, ZoomOut, Layout, RotateCcw, Copy, Lock, Unlock, 
  Eye, EyeOff, ChevronUp, ChevronDown, MousePointer,
  Music, Wine, Mic2, Users, Armchair, RotateCw, Hexagon,
  Triangle, Star, Diamond, Palette, Clipboard, ClipboardPaste,
  Group, Ungroup, Crown, Anchor, Pencil, Check, Gift, Sparkles
} from 'lucide-react';
import {
  CANVAS_SHAPE_OPTIONS,
  SHAPE_POLYGONS,
  DEFAULT_CUSTOM_POLYGON,
  getCanvasClipPath,
  getCanvasBorderRadius,
} from '../../lib/venueShapes';
import {
  hasTemplate as hasNauticalTemplate,
  getTemplateElements,
  TEMPLATE_LABELS,
} from '../../lib/venueTemplates';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useProducerAuth } from '../../context/ProducerAuthContext';

const COLORS = [
  '#D4AF37', '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#10B981', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6',
  '#A855F7', '#EC4899', '#F43F5E', '#78716C', '#1F2937'
];

const ELEMENT_TYPES = {
  section: { label: 'Sección', icon: Users, hasCapacity: true, hasPrice: true, category: 'main' },
  stage: { label: 'Escenario', icon: Mic2, hasCapacity: false, hasPrice: false, category: 'main' },
  djbooth: { label: 'Cabina DJ', icon: Music, hasCapacity: false, hasPrice: false, category: 'main' },
  bar: { label: 'Barra', icon: Wine, hasCapacity: true, hasPrice: false, category: 'main' },
  table: { label: 'Mesa', icon: Circle, hasCapacity: true, hasPrice: true, category: 'main' },
  lounge: { label: 'Lounge/VIP', icon: Armchair, hasCapacity: true, hasPrice: true, category: 'main' },
  // Drawing/Structure elements
  wall: { label: 'Pared', icon: 'wall', hasCapacity: false, hasPrice: false, category: 'structure', color: '#52525B' },
  wall_thick: { label: 'Pared Gruesa', icon: 'wall_thick', hasCapacity: false, hasPrice: false, category: 'structure', color: '#3F3F46' },
  wall_thin: { label: 'Línea Fina', icon: 'wall_thin', hasCapacity: false, hasPrice: false, category: 'structure', color: '#71717A' },
  wall_dashed: { label: 'Línea Punteada', icon: 'wall_dashed', hasCapacity: false, hasPrice: false, category: 'structure', color: '#A1A1AA' },
  corner_wall: { label: 'Esquina', icon: 'corner_wall', hasCapacity: false, hasPrice: false, category: 'structure', color: '#52525B' },
  pillar: { label: 'Pilar', icon: 'pillar', hasCapacity: false, hasPrice: false, category: 'structure', color: '#3F3F46' },
  circle_deco: { label: 'Círculo', icon: 'circle_deco', hasCapacity: false, hasPrice: false, category: 'structure', color: '#71717A' },
  rect_deco: { label: 'Rectángulo', icon: 'rect_deco', hasCapacity: false, hasPrice: false, category: 'structure', color: '#71717A' },
  area: { label: 'Área/Zona', icon: 'area', hasCapacity: false, hasPrice: false, category: 'structure', color: '#27272A' },
  // Guide elements
  exit: { label: 'Salida Emergencia', icon: 'exit', hasCapacity: false, hasPrice: false, category: 'guide', color: '#22C55E' },
  entrance: { label: 'Entrada', icon: 'entrance', hasCapacity: false, hasPrice: false, category: 'guide', color: '#3B82F6' },
  bathroom: { label: 'Baños', icon: 'bathroom', hasCapacity: false, hasPrice: false, category: 'guide', color: '#06B6D4' },
  stairs: { label: 'Escaleras', icon: 'stairs', hasCapacity: false, hasPrice: false, category: 'guide', color: '#8B5CF6' },
  elevator: { label: 'Ascensor', icon: 'elevator', hasCapacity: false, hasPrice: false, category: 'guide', color: '#6366F1' },
  wardrobe: { label: 'Guardarropa', icon: 'wardrobe', hasCapacity: false, hasPrice: false, category: 'guide', color: '#EC4899' },
  smoking: { label: 'Zona Fumadores', icon: 'smoking', hasCapacity: false, hasPrice: false, category: 'guide', color: '#78716C' },
  firstaid: { label: 'Primeros Auxilios', icon: 'firstaid', hasCapacity: false, hasPrice: false, category: 'guide', color: '#EF4444' },
  info: { label: 'Información', icon: 'info', hasCapacity: false, hasPrice: false, category: 'guide', color: '#EAB308' },
  parking: { label: 'Estacionamiento', icon: 'parking', hasCapacity: false, hasPrice: false, category: 'guide', color: '#1F2937' },
  foodcourt: { label: 'Zona Comida', icon: 'foodcourt', hasCapacity: false, hasPrice: false, category: 'guide', color: '#F97316' },
  wheelchair: { label: 'Accesibilidad', icon: 'wheelchair', hasCapacity: false, hasPrice: false, category: 'guide', color: '#0EA5E9' },
};

const SHAPES = [
  { id: 'rect', label: 'Rectángulo', icon: Square },
  { id: 'rounded', label: 'Redondeado', icon: Square },
  { id: 'circle', label: 'Círculo', icon: Circle },
  { id: 'hexagon', label: 'Hexágono', icon: Hexagon },
  { id: 'triangle', label: 'Triángulo', icon: Triangle },
  { id: 'diamond', label: 'Diamante', icon: Diamond },
  { id: 'star', label: 'Estrella', icon: Star },
  { id: 'trapezoid', label: 'Trapecio', icon: Square },
  { id: 'arc', label: 'Arco', icon: Circle },
  { id: 'lshape', label: 'L-Shape', icon: Square },
];

const STYLES = [
  { id: 'classic', label: 'Clásico', description: 'Simple y limpio', gradient: false, pattern: 'none', opacity: 0.7 },
  { id: 'modern', label: 'Moderno', description: 'Gradientes suaves y puntos', gradient: true, pattern: 'dots', opacity: 0.75, gradientType: 'diagonal' },
  { id: 'minimalist', label: 'Minimalista', description: 'Colores sólidos limpios', gradient: false, pattern: 'none', opacity: 0.5, stroke: 'dashed' },
  { id: 'futuristic', label: 'Futurista', description: 'Efectos cyber grid', gradient: true, pattern: 'grid', opacity: 0.6, glow: true },
  { id: 'neon', label: 'Neón', description: 'Resplandor vibrante', gradient: false, pattern: 'none', opacity: 0.4, glow: true, neonBorder: true },
  { id: 'glass', label: 'Cristal', description: 'Glassmorphism', gradient: false, pattern: 'none', opacity: 0.3, blur: true },
  // New premium styles
  { id: 'brutalist', label: 'Brutalista', description: 'Industrial y audaz', gradient: false, pattern: 'noise', opacity: 0.85, thickBorder: true, roughEdge: true },
  { id: 'holographic', label: 'Holográfico', description: 'Iridiscente y dinámico', gradient: true, pattern: 'holo', opacity: 0.6, holographic: true },
  { id: 'luxury', label: 'Luxury', description: 'Acabado premium metálico', gradient: true, pattern: 'metallic', opacity: 0.9, metallic: true, goldTrim: true },
];

// Preset backgrounds for venue maps - Premium Professional Quality
const BACKGROUND_PRESETS = [
  { id: 'none', label: 'Negro Puro', color: '#000000', pattern: 'none' },
  { id: 'minimal-dots', label: 'Minimalista', color: '#080808', pattern: 'minimal' },
  { id: 'neo-grid', label: 'Neo Grid', color: '#0A0A0C', pattern: 'neo-grid' },
  { id: 'blueprint-pro', label: 'Blueprint Pro', color: '#071520', pattern: 'blueprint' },
  { id: 'cyber-dark', label: 'Cyber Dark', color: '#0A0812', pattern: 'cyber' },
  { id: 'gradient-violet', label: 'Violeta Ambient', color: '#0D0A14', pattern: 'gradient-violet' },
  { id: 'gradient-warm', label: 'Cálido Premium', color: '#12100E', pattern: 'gradient-warm' },
  { id: 'carbon-fiber', label: 'Fibra Carbono', color: '#0C0C0C', pattern: 'carbon' },
  { id: 'hex-pattern', label: 'Hexagonal', color: '#080A0C', pattern: 'hexagon' },
  { id: 'studio-floor', label: 'Estudio Pro', color: '#0E0E0E', pattern: 'studio' },
];

const getShapePath = (shape, width, height) => {
  const w = width, h = height;
  switch (shape) {
    case 'rect': return `M 4 4 L ${w-4} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
    case 'rounded': const r = Math.min(16, w/4, h/4); return `M ${r+4} 4 L ${w-r-4} 4 Q ${w-4} 4 ${w-4} ${r+4} L ${w-4} ${h-r-4} Q ${w-4} ${h-4} ${w-r-4} ${h-4} L ${r+4} ${h-4} Q 4 ${h-4} 4 ${h-r-4} L 4 ${r+4} Q 4 4 ${r+4} 4 Z`;
    case 'circle': return `M ${w/2} 4 A ${w/2-4} ${h/2-4} 0 1 1 ${w/2} ${h-4} A ${w/2-4} ${h/2-4} 0 1 1 ${w/2} 4 Z`;
    case 'hexagon': return `M ${w*0.25} 4 L ${w*0.75} 4 L ${w-4} ${h/2} L ${w*0.75} ${h-4} L ${w*0.25} ${h-4} L 4 ${h/2} Z`;
    case 'triangle': return `M ${w/2} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
    case 'diamond': return `M ${w/2} 4 L ${w-4} ${h/2} L ${w/2} ${h-4} L 4 ${h/2} Z`;
    case 'star': { let p = ''; for (let i = 0; i < 10; i++) { const r2 = i % 2 === 0 ? Math.min(w,h)/2-4 : (Math.min(w,h)/2-4)*0.4; const a = (i*Math.PI/5)-Math.PI/2; p += (i===0?'M ':'L ')+(w/2+r2*Math.cos(a))+' '+(h/2+r2*Math.sin(a))+' '; } return p+'Z'; }
    case 'trapezoid': return `M ${w*0.15} ${h-4} L ${w*0.05} 4 L ${w*0.95} 4 L ${w*0.85} ${h-4} Z`;
    case 'arc': return `M 4 ${h} A ${w/2-4} ${h-8} 0 0 1 ${w-4} ${h} L ${w*0.85} ${h} A ${w*0.35} ${h*0.7} 0 0 0 ${w*0.15} ${h} Z`;
    case 'lshape': return `M 4 4 L ${w*0.4} 4 L ${w*0.4} ${h*0.5} L ${w-4} ${h*0.5} L ${w-4} ${h-4} L 4 ${h-4} Z`;
    default: return `M 4 4 L ${w-4} 4 L ${w-4} ${h-4} L 4 ${h-4} Z`;
  }
};

const ElementShape = ({ element, width, height, isSelected }) => {
  const { type, color, name, capacity, shape = 'rect', style = 'modern' } = element;
  const styleConfig = STYLES.find(s => s.id === style) || STYLES[0];
  const shapePath = getShapePath(shape, width, height);
  
  // Generate style-aware fill
  const getStyledFill = () => {
    if (styleConfig.holographic) return `url(#grad-${element.id})`;
    if (styleConfig.metallic) return `url(#grad-${element.id})`;
    if (styleConfig.gradient || styleConfig.gradientType) return `url(#grad-${element.id})`;
    return `${color}${Math.round(styleConfig.opacity * 255).toString(16).padStart(2, '0')}`;
  };
  
  // Generate style-aware stroke
  const getStyledStroke = () => {
    if (styleConfig.neonBorder) return color;
    if (styleConfig.goldTrim) return '#D4AF37';
    if (styleConfig.thickBorder) return color;
    return isSelected ? '#fff' : `${color}cc`;
  };
  
  const getStrokeWidth = () => {
    if (styleConfig.thickBorder) return 4;
    if (styleConfig.neonBorder) return 3;
    if (styleConfig.goldTrim) return 2;
    return isSelected ? 3 : 2;
  };

  // Common style defs that apply to ALL element types
  const renderStyleDefs = () => (
    <defs>
      {/* Modern style - diagonal gradient */}
      {styleConfig.gradientType === 'diagonal' && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={styleConfig.opacity + 0.15} />
          <stop offset="50%" stopColor={color} stopOpacity={styleConfig.opacity} />
          <stop offset="100%" stopColor={color} stopOpacity={styleConfig.opacity - 0.2} />
        </linearGradient>
      )}
      {/* Classic style - vertical gradient with gold accent */}
      {styleConfig.gradientType === 'vertical' && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={styleConfig.goldAccent ? '#D4AF37' : color} stopOpacity={0.4} />
          <stop offset="20%" stopColor={color} stopOpacity={styleConfig.opacity} />
          <stop offset="80%" stopColor={color} stopOpacity={styleConfig.opacity - 0.1} />
          <stop offset="100%" stopColor={styleConfig.goldAccent ? '#B8860B' : color} stopOpacity={0.5} />
        </linearGradient>
      )}
      {/* Futuristic gradient */}
      {styleConfig.glow && !styleConfig.neonBorder && !styleConfig.holographic && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={styleConfig.opacity + 0.2} />
          <stop offset="50%" stopColor={color} stopOpacity={styleConfig.opacity - 0.1} />
          <stop offset="100%" stopColor={color} stopOpacity={styleConfig.opacity + 0.1} />
        </linearGradient>
      )}
      {/* Neon style - solid with glow */}
      {styleConfig.neonBorder && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={styleConfig.opacity + 0.1} />
          <stop offset="100%" stopColor={color} stopOpacity={styleConfig.opacity - 0.1} />
        </linearGradient>
      )}
      {/* Glass style - very transparent */}
      {styleConfig.blur && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity={0.15} />
          <stop offset="100%" stopColor={color} stopOpacity={0.2} />
        </linearGradient>
      )}
      {/* Brutalist - solid harsh */}
      {styleConfig.roughEdge && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.95} />
          <stop offset="100%" stopColor={color} stopOpacity={0.85} />
        </linearGradient>
      )}
      {/* Holographic gradient - rainbow */}
      {styleConfig.holographic && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" stopOpacity={0.7} />
          <stop offset="20%" stopColor="#8b5cf6" stopOpacity={0.7} />
          <stop offset="40%" stopColor="#06b6d4" stopOpacity={0.7} />
          <stop offset="60%" stopColor="#22c55e" stopOpacity={0.7} />
          <stop offset="80%" stopColor="#eab308" stopOpacity={0.7} />
          <stop offset="100%" stopColor="#ec4899" stopOpacity={0.7} />
        </linearGradient>
      )}
      {/* Metallic gradient for Luxury */}
      {styleConfig.metallic && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.95} />
          <stop offset="25%" stopColor={color} stopOpacity={0.85} />
          <stop offset="50%" stopColor="#FFD700" stopOpacity={0.6} />
          <stop offset="75%" stopColor={color} stopOpacity={0.85} />
          <stop offset="100%" stopColor="#B8860B" stopOpacity={0.9} />
        </linearGradient>
      )}
      {/* Default gradient fallback */}
      {styleConfig.gradient && !styleConfig.gradientType && !styleConfig.glow && !styleConfig.blur && !styleConfig.roughEdge && !styleConfig.holographic && !styleConfig.metallic && !styleConfig.neonBorder && (
        <linearGradient id={`grad-${element.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={styleConfig.opacity + 0.2} />
          <stop offset="100%" stopColor={color} stopOpacity={styleConfig.opacity - 0.2} />
        </linearGradient>
      )}
      {/* PATTERNS */}
      {styleConfig.pattern === 'dots' && <pattern id={`pat-${element.id}`} width="8" height="8" patternUnits="userSpaceOnUse"><circle cx="4" cy="4" r="1.5" fill="rgba(255,255,255,0.15)"/></pattern>}
      {styleConfig.pattern === 'grid' && <pattern id={`pat-${element.id}`} width="12" height="12" patternUnits="userSpaceOnUse"><path d="M 12 0 L 0 0 0 12" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8"/></pattern>}
      {styleConfig.pattern === 'lines' && <pattern id={`pat-${element.id}`} width="6" height="6" patternUnits="userSpaceOnUse"><line x1="0" y1="6" x2="6" y2="0" stroke={styleConfig.goldAccent ? 'rgba(212,175,55,0.25)' : 'rgba(255,255,255,0.1)'} strokeWidth="1.5"/></pattern>}
      {styleConfig.pattern === 'noise' && <pattern id={`pat-${element.id}`} width="6" height="6" patternUnits="userSpaceOnUse"><rect x="0" y="0" width="2" height="2" fill="rgba(255,255,255,0.08)"/><rect x="3" y="2" width="2" height="2" fill="rgba(0,0,0,0.15)"/><rect x="1" y="4" width="2" height="2" fill="rgba(255,255,255,0.05)"/><rect x="4" y="0" width="2" height="2" fill="rgba(0,0,0,0.1)"/></pattern>}
      {styleConfig.pattern === 'holo' && <pattern id={`pat-${element.id}`} width="16" height="16" patternUnits="userSpaceOnUse"><line x1="0" y1="0" x2="16" y2="16" stroke="rgba(255,255,255,0.15)" strokeWidth="0.8"/><line x1="16" y1="0" x2="0" y2="16" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8"/><circle cx="8" cy="8" r="3" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5"/></pattern>}
      {styleConfig.pattern === 'metallic' && <pattern id={`pat-${element.id}`} width="4" height="4" patternUnits="userSpaceOnUse"><line x1="0" y1="2" x2="4" y2="2" stroke="rgba(212,175,55,0.3)" strokeWidth="1"/></pattern>}
      {/* Glow filter for neon */}
      {(styleConfig.glow || styleConfig.neonBorder) && (
        <filter id={`glow-${element.id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      )}
      {/* Glass blur effect */}
      {styleConfig.blur && (
        <filter id={`blur-${element.id}`}>
          <feGaussianBlur stdDeviation="1" />
        </filter>
      )}
    </defs>
  );
  
  // Render style overlays (patterns, effects)
  const renderStyleOverlays = (path) => (
    <>
      {styleConfig.pattern && styleConfig.pattern !== 'none' && <path d={path} fill={`url(#pat-${element.id})`}/>}
      {styleConfig.goldAccent && <path d={path} fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.5" transform="scale(0.92) translate(4,4)"/>}
      {styleConfig.neonBorder && <path d={path} fill="none" stroke={color} strokeWidth="6" opacity="0.2" filter={`url(#glow-${element.id})`}/>}
      {styleConfig.glow && !styleConfig.neonBorder && <path d={path} fill="none" stroke={color} strokeWidth="4" opacity="0.15" filter={`url(#glow-${element.id})`}/>}
      {styleConfig.metallic && <path d={path} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" transform="scale(0.95) translate(3,3)"/>}
      {styleConfig.blur && <rect x="0" y="0" width={width} height={height} fill="rgba(255,255,255,0.05)" rx="8"/>}
    </>
  );
  
  const fillStyle = getStyledFill();
  const strokeStyle = getStyledStroke();
  const strokeWidth = getStrokeWidth();

  if (type === 'bar') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {renderStyleDefs()}
        <rect x={4} y={height*0.3} width={width-8} height={height*0.65} rx={6} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}/>
        {renderStyleOverlays(`M 4 ${height*0.3+6} Q 4 ${height*0.3} 10 ${height*0.3} L ${width-10} ${height*0.3} Q ${width-4} ${height*0.3} ${width-4} ${height*0.3+6} L ${width-4} ${height*0.95-6} Q ${width-4} ${height*0.95} ${width-10} ${height*0.95} L 10 ${height*0.95} Q 4 ${height*0.95} 4 ${height*0.95-6} Z`)}
        <rect x={2} y={height*0.25} width={width-4} height={height*0.12} rx={4} fill={styleConfig.metallic ? '#D4AF37' : color} opacity="0.9"/>
        <rect x={6} y={height*0.28} width={width-12} height={height*0.06} rx={2} fill="rgba(255,255,255,0.3)"/>
        {width > 60 && (
          <g>
            {[0.15, 0.3, 0.45, 0.6, 0.75, 0.85].slice(0, Math.floor(width/25)).map((pos, i) => (
              <g key={i}>
                <rect x={width*pos-4} y={height*0.4} width={8} height={height*0.25} rx={2} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899'][i%6] : ['#22c55e','#ef4444','#3b82f6','#eab308','#8b5cf6','#ec4899'][i%6]} opacity="0.8"/>
                <rect x={width*pos-2} y={height*0.38} width={4} height={height*0.06} rx={1} fill="rgba(255,255,255,0.5)"/>
              </g>
            ))}
          </g>
        )}
        {width > 80 && (
          <g>
            {[0.25, 0.5, 0.75].map((pos, i) => (
              <g key={i} transform={`translate(${width*pos-6}, ${height*0.7})`}>
                <path d="M 0 12 L 2 0 L 10 0 L 12 12 L 10 12 L 9 14 L 3 14 L 2 12 Z" fill={styleConfig.metallic ? 'rgba(212,175,55,0.6)' : 'rgba(255,255,255,0.4)'} stroke={styleConfig.goldTrim ? '#D4AF37' : 'rgba(255,255,255,0.6)'} strokeWidth="0.5"/>
              </g>
            ))}
          </g>
        )}
        <rect x={width/2-30} y={height*0.85} width={60} height={16} rx={3} fill={styleConfig.metallic ? 'rgba(212,175,55,0.8)' : 'rgba(0,0,0,0.6)'}/>
        <text x={width/2} y={height*0.93} textAnchor="middle" fill="#fff" fontSize={Math.min(10, width/12)} fontWeight="bold">{name}</text>
      </svg>
    );
  }

  if (type === 'djbooth') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {renderStyleDefs()}
        <path d={shape === 'arc' ? getShapePath('arc', width, height) : shapePath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}/>
        {renderStyleOverlays(shape === 'arc' ? getShapePath('arc', width, height) : shapePath)}
        <circle cx={width*0.28} cy={height*0.45} r={Math.min(width*0.12, 14)} fill={styleConfig.metallic ? '#1f1f1f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="2"/>
        <circle cx={width*0.28} cy={height*0.45} r={Math.min(width*0.06, 7)} fill={styleConfig.holographic ? '#ec4899' : color} opacity="0.6"/>
        <circle cx={width*0.72} cy={height*0.45} r={Math.min(width*0.12, 14)} fill={styleConfig.metallic ? '#1f1f1f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="2"/>
        <circle cx={width*0.72} cy={height*0.45} r={Math.min(width*0.06, 7)} fill={styleConfig.holographic ? '#06b6d4' : color} opacity="0.6"/>
        <rect x={width*0.38} y={height*0.3} width={width*0.24} height={height*0.35} rx={3} fill={styleConfig.metallic ? '#0f0f0f' : '#1f2937'} stroke={styleConfig.goldTrim ? '#D4AF37' : color} strokeWidth="1"/>
        {[0.35, 0.45, 0.55, 0.65].map((pos, i) => (
          <rect key={i} x={width*0.4+i*width*0.05} y={height*0.35} width={3} height={height*0.2} rx={1} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e'][i] : color} opacity="0.7"/>
        ))}
        <text x={width/2} y={height*0.85} textAnchor="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(11, width/12)} fontWeight="bold">{name}</text>
      </svg>
    );
  }

  if (type === 'stage') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {renderStyleDefs()}
        <path d={shape === 'trapezoid' ? getShapePath('trapezoid', width, height) : shapePath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}/>
        {renderStyleOverlays(shape === 'trapezoid' ? getShapePath('trapezoid', width, height) : shapePath)}
        <rect x={width*0.05} y={height*0.2} width={width*0.9} height={height*0.1} rx={2} fill={styleConfig.metallic ? '#D4AF37' : '#4B5563'}/>
        {width > 100 && [0.15, 0.3, 0.5, 0.7, 0.85].map((pos, i) => (
          <circle key={i} cx={width*pos} cy={height*0.12} r={5} fill={styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308'][i] : (styleConfig.metallic ? '#FFD700' : '#FCD34D')} opacity="0.9"/>
        ))}
        <text x={width/2} y={height*0.65} textAnchor="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(13, width/10)} fontWeight="bold">{name}</text>
      </svg>
    );
  }

  if (type === 'table') {
    const chairCount = Math.min(Math.max(4, capacity || 8), 12);
    const tableRadius = Math.min(width, height) * 0.3;
    const chairRadius = Math.min(width, height) * 0.08;
    
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {renderStyleDefs()}
        {Array.from({ length: chairCount }).map((_, i) => {
          const angle = (i / chairCount) * 2 * Math.PI - Math.PI/2;
          const chairColor = styleConfig.holographic ? ['#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899','#8b5cf6','#06b6d4','#22c55e','#eab308','#ec4899','#8b5cf6'][i] : color;
          return (
            <circle key={i} cx={width/2 + Math.cos(angle) * (tableRadius + chairRadius + 5)} cy={height/2 + Math.sin(angle) * (tableRadius + chairRadius + 5)} r={chairRadius} fill={`${chairColor}40`} stroke={styleConfig.goldTrim ? '#D4AF37' : chairColor} strokeWidth={1}/>
          );
        })}
        <circle cx={width/2} cy={height/2} r={tableRadius} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}/>
        {styleConfig.pattern && styleConfig.pattern !== 'none' && <circle cx={width/2} cy={height/2} r={tableRadius-2} fill={`url(#pat-${element.id})`}/>}
        {styleConfig.neonBorder && <circle cx={width/2} cy={height/2} r={tableRadius} fill="none" stroke={color} strokeWidth="6" opacity="0.2" filter={`url(#glow-${element.id})`}/>}
        {styleConfig.goldAccent && <circle cx={width/2} cy={height/2} r={tableRadius*0.9} fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.5"/>}
        <text x={width/2} y={height/2+4} textAnchor="middle" dominantBaseline="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(12, tableRadius*0.5)} fontWeight="bold">{name.replace('Mesa ', '')}</text>
      </svg>
    );
  }

  if (type === 'lounge') {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {renderStyleDefs()}
        <path d={shapePath} fill={fillStyle} stroke={strokeStyle} strokeWidth={strokeWidth} strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}/>
        {renderStyleOverlays(shapePath)}
        {height > 50 && <rect x={10} y={height-16} width={width-20} height={8} rx={4} fill={styleConfig.metallic ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.15)'}/>}
        {height > 50 && <rect x={10} y={8} width={width-20} height={8} rx={4} fill={styleConfig.metallic ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.15)'}/>}
        <rect x={width/2-20} y={height/2-10} width={40} height={20} rx={3} fill={styleConfig.metallic ? 'rgba(212,175,55,0.9)' : (styleConfig.holographic ? 'rgba(139,92,246,0.8)' : 'rgba(0,0,0,0.5)')}/>
        <text x={width/2} y={height/2+1} textAnchor="middle" dominantBaseline="middle" fill={styleConfig.holographic ? '#fff' : '#D4AF37'} fontSize="10" fontWeight="bold">VIP</text>
        {capacity > 0 && height > 60 && <text x={width/2} y={height/2+18} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9">{capacity} cap.</text>}
      </svg>
    );
  }

  // Guide elements rendering
  const guideTypeConfig = ELEMENT_TYPES[type];
  if (guideTypeConfig?.category === 'guide') {
    const iconColor = guideTypeConfig.color || color;
    const iconSize = Math.min(width, height) * 0.5;
    const cx = width / 2;
    const cy = height / 2 - 5;
    
    const getGuideIcon = () => {
      switch(type) {
        case 'exit':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.3} ${cy} L ${cx+iconSize*0.2} ${cy} M ${cx+iconSize*0.05} ${cy-iconSize*0.15} L ${cx+iconSize*0.2} ${cy} L ${cx+iconSize*0.05} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
              <rect x={cx+iconSize*0.25} y={cy-iconSize*0.35} width={iconSize*0.15} height={iconSize*0.7} fill={iconColor} opacity="0.6"/>
            </>
          );
        case 'entrance':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx+iconSize*0.2} ${cy} L ${cx-iconSize*0.2} ${cy} M ${cx-iconSize*0.05} ${cy-iconSize*0.15} L ${cx-iconSize*0.2} ${cy} L ${cx-iconSize*0.05} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'bathroom':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              {/* Male icon */}
              <circle cx={cx-iconSize*0.18} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <rect x={cx-iconSize*0.23} y={cy-iconSize*0.05} width={iconSize*0.1} height={iconSize*0.25} rx={2} fill={iconColor}/>
              <path d={`M ${cx-iconSize*0.23} ${cy+iconSize*0.2} L ${cx-iconSize*0.23} ${cy+iconSize*0.35} M ${cx-iconSize*0.13} ${cy+iconSize*0.2} L ${cx-iconSize*0.13} ${cy+iconSize*0.35}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
              {/* Female icon */}
              <circle cx={cx+iconSize*0.18} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.08} ${cy+iconSize*0.2} L ${cx+iconSize*0.18} ${cy-iconSize*0.05} L ${cx+iconSize*0.28} ${cy+iconSize*0.2} Z`} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.13} ${cy+iconSize*0.2} L ${cx+iconSize*0.13} ${cy+iconSize*0.35} M ${cx+iconSize*0.23} ${cy+iconSize*0.2} L ${cx+iconSize*0.23} ${cy+iconSize*0.35}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
            </>
          );
        case 'stairs':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.3} ${cy+iconSize*0.3} L ${cx-iconSize*0.3} ${cy+iconSize*0.1} L ${cx-iconSize*0.1} ${cy+iconSize*0.1} L ${cx-iconSize*0.1} ${cy-iconSize*0.1} L ${cx+iconSize*0.1} ${cy-iconSize*0.1} L ${cx+iconSize*0.1} ${cy-iconSize*0.3} L ${cx+iconSize*0.3} ${cy-iconSize*0.3}`} stroke={iconColor} strokeWidth="3" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'elevator':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <rect x={cx-iconSize*0.35} y={cy-iconSize*0.35} width={iconSize*0.7} height={iconSize*0.7} rx={2} stroke={iconColor} strokeWidth="2" fill="none"/>
              <path d={`M ${cx-iconSize*0.15} ${cy} L ${cx} ${cy-iconSize*0.2} L ${cx+iconSize*0.15} ${cy}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
              <path d={`M ${cx-iconSize*0.15} ${cy+iconSize*0.1} L ${cx} ${cy+iconSize*0.3} L ${cx+iconSize*0.15} ${cy+iconSize*0.1}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
            </>
          );
        case 'wardrobe':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <path d={`M ${cx-iconSize*0.25} ${cy-iconSize*0.25} L ${cx} ${cy-iconSize*0.35} L ${cx+iconSize*0.25} ${cy-iconSize*0.25} L ${cx+iconSize*0.2} ${cy+iconSize*0.25} L ${cx-iconSize*0.2} ${cy+iconSize*0.25} Z`} fill={iconColor} opacity="0.6"/>
              <circle cx={cx} cy={cy-iconSize*0.15} r={iconSize*0.12} fill="none" stroke={iconColor} strokeWidth="2"/>
            </>
          );
        case 'smoking':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <rect x={cx-iconSize*0.35} y={cy} width={iconSize*0.5} height={iconSize*0.12} rx={2} fill={iconColor}/>
              <path d={`M ${cx+iconSize*0.2} ${cy+iconSize*0.06} Q ${cx+iconSize*0.2} ${cy-iconSize*0.15} ${cx+iconSize*0.3} ${cy-iconSize*0.15} Q ${cx+iconSize*0.35} ${cy-iconSize*0.05} ${cx+iconSize*0.35} ${cy+iconSize*0.06}`} stroke={iconColor} strokeWidth="2" fill="none"/>
            </>
          );
        case 'firstaid':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.3"/>
              <rect x={cx-iconSize*0.08} y={cy-iconSize*0.3} width={iconSize*0.16} height={iconSize*0.6} rx={2} fill={iconColor}/>
              <rect x={cx-iconSize*0.3} y={cy-iconSize*0.08} width={iconSize*0.6} height={iconSize*0.16} rx={2} fill={iconColor}/>
            </>
          );
        case 'info':
          return (
            <>
              <circle cx={cx} cy={cy} r={iconSize*0.4} fill={iconColor} opacity="0.2" stroke={iconColor} strokeWidth="2"/>
              <circle cx={cx} cy={cy-iconSize*0.18} r={iconSize*0.06} fill={iconColor}/>
              <rect x={cx-iconSize*0.05} y={cy-iconSize*0.05} width={iconSize*0.1} height={iconSize*0.3} rx={2} fill={iconColor}/>
            </>
          );
        case 'parking':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <text x={cx} y={cy+iconSize*0.15} textAnchor="middle" fill={iconColor} fontSize={iconSize*0.6} fontWeight="bold">P</text>
            </>
          );
        case 'foodcourt':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <circle cx={cx} cy={cy} r={iconSize*0.25} stroke={iconColor} strokeWidth="2" fill="none"/>
              <path d={`M ${cx-iconSize*0.1} ${cy-iconSize*0.15} L ${cx-iconSize*0.1} ${cy+iconSize*0.15} M ${cx+iconSize*0.1} ${cy-iconSize*0.15} L ${cx+iconSize*0.1} ${cy+iconSize*0.15}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
              <path d={`M ${cx-iconSize*0.1} ${cy-iconSize*0.05} L ${cx+iconSize*0.1} ${cy-iconSize*0.05}`} stroke={iconColor} strokeWidth="2"/>
            </>
          );
        case 'wheelchair':
          return (
            <>
              <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.2"/>
              <circle cx={cx-iconSize*0.05} cy={cy-iconSize*0.2} r={iconSize*0.1} fill={iconColor}/>
              <path d={`M ${cx-iconSize*0.05} ${cy-iconSize*0.08} L ${cx-iconSize*0.05} ${cy+iconSize*0.1} L ${cx+iconSize*0.15} ${cy+iconSize*0.1}`} stroke={iconColor} strokeWidth="2" strokeLinecap="round" fill="none"/>
              <circle cx={cx-iconSize*0.05} cy={cy+iconSize*0.2} r={iconSize*0.15} stroke={iconColor} strokeWidth="2" fill="none"/>
            </>
          );
        default:
          return <rect x={cx-iconSize/2} y={cy-iconSize/2} width={iconSize} height={iconSize} rx={4} fill={iconColor} opacity="0.3"/>;
      }
    };
    
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {getGuideIcon()}
        <text x={width/2} y={height - 8} textAnchor="middle" fill="#fff" fontSize={Math.min(10, width/10)} fontWeight="500" style={{textShadow:'0 1px 2px rgba(0,0,0,0.8)'}}>{name}</text>
      </svg>
    );
  }

  // Structure elements rendering (walls, pillars, decorative shapes, areas)
  const structureTypeConfig = ELEMENT_TYPES[type];
  if (structureTypeConfig?.category === 'structure') {
    const structColor = color || structureTypeConfig.color;
    
    // Standard wall
    if (type === 'wall') {
      const thickness = Math.min(height, 8);
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <rect x="0" y={(height - thickness) / 2} width={width} height={thickness} rx={1} fill={structColor} />
          <rect x="0" y={(height - thickness) / 2} width={width} height={thickness} rx={1} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        </svg>
      );
    }
    
    // Thick wall
    if (type === 'wall_thick') {
      const thickness = Math.min(height, 16);
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <rect x="0" y={(height - thickness) / 2} width={width} height={thickness} rx={2} fill={structColor} />
          <rect x="2" y={(height - thickness) / 2 + 2} width={width - 4} height={thickness - 4} fill="rgba(0,0,0,0.2)" rx={1} />
          <rect x="0" y={(height - thickness) / 2} width={width} height={thickness} rx={2} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        </svg>
      );
    }
    
    // Thin line
    if (type === 'wall_thin') {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke={structColor} strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    }
    
    // Dashed line
    if (type === 'wall_dashed') {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke={structColor} strokeWidth="3" strokeDasharray="8 6" strokeLinecap="round" />
        </svg>
      );
    }
    
    // Corner wall (L-shape)
    if (type === 'corner_wall') {
      const t = 8; // thickness
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <path 
            d={`M 0 0 L ${t} 0 L ${t} ${height - t} L ${width} ${height - t} L ${width} ${height} L 0 ${height} Z`} 
            fill={structColor}
          />
          <path 
            d={`M 0 0 L ${t} 0 L ${t} ${height - t} L ${width} ${height - t} L ${width} ${height} L 0 ${height} Z`} 
            fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1"
          />
        </svg>
      );
    }
    
    // Pillar
    if (type === 'pillar') {
      const r = Math.min(width, height) / 2 - 1;
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <circle cx={width/2} cy={height/2} r={r} fill={structColor} />
          <circle cx={width/2} cy={height/2} r={r * 0.6} fill="rgba(0,0,0,0.25)" />
          <circle cx={width/2} cy={height/2} r={r} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
        </svg>
      );
    }
    
    if (type === 'circle_deco') {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <circle cx={width/2} cy={height/2} r={Math.min(width, height)/2 - 2} fill="none" stroke={structColor} strokeWidth="2" strokeDasharray="6 3" />
        </svg>
      );
    }
    
    if (type === 'rect_deco') {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <rect x="2" y="2" width={width - 4} height={height - 4} rx={4} fill="none" stroke={structColor} strokeWidth="2" strokeDasharray="8 4" />
        </svg>
      );
    }
    
    if (type === 'area') {
      return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          <rect x="0" y="0" width={width} height={height} rx={4} fill={structColor} opacity="0.15" />
          <rect x="0" y="0" width={width} height={height} rx={4} fill="none" stroke={structColor} strokeWidth="1" strokeDasharray="4 2" opacity="0.4" />
          <text x={width/2} y={height/2} textAnchor="middle" dominantBaseline="middle" fill={structColor} fontSize={Math.min(11, width/10)} opacity="0.6">{name}</text>
        </svg>
      );
    }
  }

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {renderStyleDefs()}
      {/* Main shape */}
      <path 
        d={shapePath} 
        fill={fillStyle} 
        stroke={strokeStyle} 
        strokeWidth={strokeWidth} 
        strokeDasharray={styleConfig.stroke === 'dashed' ? "6 3" : "none"}
      />
      {/* Apply style overlays (patterns, glows, effects) */}
      {renderStyleOverlays(shapePath)}
      {/* Text */}
      <text x={width/2} y={height/2-4} textAnchor="middle" dominantBaseline="middle" fill={styleConfig.metallic ? '#D4AF37' : '#fff'} fontSize={Math.min(12, width/10)} fontWeight="bold" style={{textShadow:'0 1px 3px rgba(0,0,0,0.8)'}}>{name}</text>
      {capacity > 0 && <text x={width/2} y={height/2+12} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize={Math.min(10, width/12)}>{capacity} cap.</text>}
    </svg>
  );
};

const VenueEditor = ({ venue = null, onSave, onCancel, eventTiers = [] }) => {
  const canvasRef = useRef(null);
  const [saving, setSaving] = useState(false);
  
  // Get subscription info from context
  const { canUseStyle, canUseBackground, canUseCanvasShape, isPremium, getPaymentLink } = useProducerAuth();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  const [venueName, setVenueName] = useState('');
  const [venueDescription, setVenueDescription] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [backgroundPreset, setBackgroundPreset] = useState('dark-grid');
  const [canvasWidth, setCanvasWidth] = useState(900);
  const [canvasHeight, setCanvasHeight] = useState(600);
  const [globalStyle, setGlobalStyle] = useState('modern');
  
  const [elements, setElements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clipboard, setClipboard] = useState(null);
  
  const [dragState, setDragState] = useState({ type: null });
  const [selectionBox, setSelectionBox] = useState(null);
  
  const [activeTool, setActiveTool] = useState('select');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentDraw, setCurrentDraw] = useState(null);
  
  const [guides, setGuides] = useState({ x: null, y: null, w: null, h: null });
  
  const [zoom, setZoom] = useState(1);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(20);
  const [canvasShape, setCanvasShape] = useState('rectangle'); // rectangle, rounded, circle, polygon, catamaran, yacht, boat, custom
  const [canvasPolygon, setCanvasPolygon] = useState(null); // Custom polygon vertices [{x,y} in 0-100 percent]
  const [editingPolygon, setEditingPolygon] = useState(false); // Toggle vertex-edit overlay
  
  const [showTemplates, setShowTemplates] = useState(false);
  const [showElementPicker, setShowElementPicker] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [rightPanelTab, setRightPanelTab] = useState('properties');
  
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [groups, setGroups] = useState([]); // Array of { id, elementIds: [] }
  
  // Multi-floor support
  const [floors, setFloors] = useState([{ id: 'floor_1', name: 'Planta Baja', order: 0 }]);
  const [activeFloor, setActiveFloor] = useState('floor_1');

  const selectedElement = selectedIds.length === 1 ? elements.find(e => e.id === selectedIds[0]) : null;
  
  // Check if selected elements belong to a group
  const selectedGroup = groups.find(g => selectedIds.some(id => g.elementIds.includes(id)));
  const canGroup = selectedIds.length > 1 && !selectedGroup;
  const canUngroup = selectedGroup && selectedIds.length > 0;

  useEffect(() => {
    if (venue) {
      setVenueName(venue.name || '');
      setVenueDescription(venue.description || '');
      setVenueAddress(venue.address || '');
      setBackgroundImage(venue.background_image || '');
      setBackgroundPreset(venue.background_preset || 'none');
      setCanvasWidth(venue.canvas_width || 900);
      setCanvasHeight(venue.canvas_height || 600);
      setCanvasShape(venue.canvas_shape || 'rectangle');
      setCanvasPolygon(venue.canvas_polygon || null);
      setGridSize(venue.grid_size || 20);
      
      // Load floors if they exist
      if (venue.floors && venue.floors.length > 0) {
        setFloors(venue.floors);
        setActiveFloor(venue.floors[0].id);
      }
      
      // Ensure all properties are loaded correctly
      const loadedElements = (venue.sections || []).map((s, i) => ({
        id: s.id || `el_${Date.now()}_${i}`,
        type: s.type || 'section',
        name: s.name || 'Elemento',
        color: s.color || COLORS[i % COLORS.length],
        capacity: s.capacity || 0,
        price: s.price || null,
        tier_id: s.tier_id || null,
        section_type: s.section_type || 'general',
        shape: s.shape || 'rect',
        style: s.style || 'modern',
        position: s.position || { x: 100, y: 100, width: 150, height: 100 },
        rotation: s.rotation || 0,
        locked: s.locked || false,
        visible: s.visible !== false,
        zIndex: s.zIndex || i
      }));
      setElements(loadedElements);
    }
  }, [venue]);

  useEffect(() => {
    api.get('/venues/templates').then(res => setTemplates(res.data.templates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.length > 0) {
        e.preventDefault();
        deleteSelected();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedIds.length > 0) {
        e.preventDefault();
        copySelected();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard) {
        e.preventDefault();
        pasteClipboard();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedIds.length > 0) {
        e.preventDefault();
        duplicateSelected();
      }
      // Group: Ctrl+G
      if ((e.ctrlKey || e.metaKey) && e.key === 'g' && !e.shiftKey && canGroup) {
        e.preventDefault();
        groupSelected();
      }
      // Ungroup: Ctrl+Shift+G
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'G' && canUngroup) {
        e.preventDefault();
        ungroupSelected();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedIds(elements.map(e => e.id));
      }
      if (e.key === 'Escape') {
        setSelectedIds([]);
        setActiveTool('select');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIds, clipboard, elements, canGroup, canUngroup]);

  const saveHistory = useCallback((newElements) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.stringify(newElements));
    setHistory(newHistory.slice(-30));
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setElements(JSON.parse(history[historyIndex - 1]));
    }
  };

  const snapValue = (value) => snapToGrid ? Math.round(value / gridSize) * gridSize : value;

  const getMousePos = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };
  };

  const addElement = (type) => {
    const typeConfig = ELEMENT_TYPES[type];
    const isGuide = typeConfig?.category === 'guide';
    const isStructure = typeConfig?.category === 'structure';
    
    const defaults = { 
      table: { shape: 'circle', w: 90, h: 90, cap: 8 }, 
      djbooth: { shape: 'arc', w: 200, h: 100, cap: 0 }, 
      stage: { shape: 'trapezoid', w: 300, h: 100, cap: 0 }, 
      bar: { shape: 'rounded', w: 180, h: 80, cap: 20 }, 
      lounge: { shape: 'rounded', w: 150, h: 120, cap: 50 }, 
      section: { shape: 'rect', w: 150, h: 120, cap: 100 },
      // Structure/Drawing elements - walls with different sizes
      wall: { shape: 'rect', w: 150, h: 8, cap: 0 },
      wall_thick: { shape: 'rect', w: 120, h: 16, cap: 0 },
      wall_thin: { shape: 'rect', w: 200, h: 3, cap: 0 },
      wall_dashed: { shape: 'rect', w: 180, h: 4, cap: 0 },
      corner_wall: { shape: 'rect', w: 60, h: 60, cap: 0 },
      pillar: { shape: 'circle', w: 25, h: 25, cap: 0 },
      circle_deco: { shape: 'circle', w: 60, h: 60, cap: 0 },
      rect_deco: { shape: 'rect', w: 80, h: 50, cap: 0 },
      area: { shape: 'rect', w: 200, h: 150, cap: 0 },
      // Guide elements - smaller sizes
      exit: { shape: 'rect', w: 60, h: 60, cap: 0 },
      entrance: { shape: 'rect', w: 60, h: 60, cap: 0 },
      bathroom: { shape: 'rect', w: 70, h: 70, cap: 0 },
      stairs: { shape: 'rect', w: 60, h: 60, cap: 0 },
      elevator: { shape: 'rect', w: 60, h: 60, cap: 0 },
      wardrobe: { shape: 'rect', w: 70, h: 60, cap: 0 },
      smoking: { shape: 'rect', w: 60, h: 60, cap: 0 },
      firstaid: { shape: 'rect', w: 60, h: 60, cap: 0 },
      info: { shape: 'rect', w: 60, h: 60, cap: 0 },
      parking: { shape: 'rect', w: 80, h: 80, cap: 0 },
      foodcourt: { shape: 'rect', w: 70, h: 70, cap: 0 },
      wheelchair: { shape: 'rect', w: 60, h: 60, cap: 0 },
    };
    const d = defaults[type] || defaults.section;
    
    const getName = () => {
      if (isGuide || isStructure) return typeConfig.label;
      if (type === 'section') return `Sección ${elements.filter(e => e.type === 'section').length + 1}`;
      if (type === 'table') return `Mesa ${elements.filter(e => e.type === 'table').length + 1}`;
      return ELEMENT_TYPES[type]?.label || 'Elemento';
    };
    
    const getColor = () => {
      if (isGuide || isStructure) return typeConfig.color;
      if (type === 'stage') return '#1F2937';
      if (type === 'djbooth') return '#6366F1';
      if (type === 'bar') return '#3B82F6';
      if (type === 'lounge') return '#D4AF37';
      return COLORS[elements.length % COLORS.length];
    };
    
    const newElement = {
      id: `el_${Date.now()}`,
      type,
      name: getName(),
      color: getColor(),
      capacity: d.cap,
      shape: d.shape,
      style: (isGuide || isStructure) ? 'minimalist' : globalStyle,
      rotation: 0,
      position: { x: 150 + (elements.length % 4) * 50, y: 150 + Math.floor(elements.length / 4) * 50, width: d.w, height: d.h },
      locked: false,
      visible: true,
      zIndex: isStructure && type === 'area' ? -1 : elements.length, // Areas go behind
      floor_id: activeFloor // Assign to current floor
    };
    
    const newElements = [...elements, newElement];
    setElements(newElements);
    saveHistory(newElements);
    setSelectedIds([newElement.id]);
    setShowElementPicker(false);
    setActiveTool('select');
  };

  const updateElement = (id, updates) => setElements(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));

  const deleteSelected = () => {
    if (selectedIds.length === 0) return;
    const newElements = elements.filter(e => !selectedIds.includes(e.id));
    setElements(newElements);
    saveHistory(newElements);
    setSelectedIds([]);
    toast.success(`${selectedIds.length} elemento(s) eliminado(s)`);
  };

  const copySelected = () => {
    const toCopy = elements.filter(e => selectedIds.includes(e.id));
    if (toCopy.length > 0) {
      setClipboard(JSON.stringify(toCopy));
      toast.success(`${toCopy.length} elemento(s) copiado(s)`);
    }
  };

  const pasteClipboard = () => {
    if (!clipboard) return;
    try {
      const items = JSON.parse(clipboard);
      const newElements = items.map((el, i) => ({
        ...el,
        id: `el_${Date.now()}_${i}`,
        position: { ...el.position, x: el.position.x + 30, y: el.position.y + 30 },
        zIndex: elements.length + i
      }));
      const updated = [...elements, ...newElements];
      setElements(updated);
      saveHistory(updated);
      setSelectedIds(newElements.map(e => e.id));
      toast.success(`${newElements.length} elemento(s) pegado(s)`);
    } catch (e) { console.error(e); }
  };

  const duplicateSelected = () => {
    const toDupe = elements.filter(e => selectedIds.includes(e.id));
    if (toDupe.length === 0) return;
    
    const newElements = toDupe.map((el, i) => ({
      ...el,
      id: `el_${Date.now()}_${i}`,
      name: `${el.name} (copia)`,
      position: { ...el.position, x: el.position.x + 30, y: el.position.y + 30 },
      zIndex: elements.length + i
    }));
    
    const updated = [...elements, ...newElements];
    setElements(updated);
    saveHistory(updated);
    setSelectedIds(newElements.map(e => e.id));
    toast.success(`${newElements.length} elemento(s) duplicado(s)`);
  };

  // Group/Ungroup functions
  const groupSelected = () => {
    if (selectedIds.length < 2) return;
    const newGroup = {
      id: `group_${Date.now()}`,
      elementIds: [...selectedIds]
    };
    setGroups(prev => [...prev, newGroup]);
    toast.success(`${selectedIds.length} elementos agrupados`);
  };

  const ungroupSelected = () => {
    if (!selectedGroup) return;
    setGroups(prev => prev.filter(g => g.id !== selectedGroup.id));
    toast.success('Grupo disuelto');
  };

  // When selecting an element that's part of a group, select all group members
  const selectWithGroup = (elementId, addToSelection = false) => {
    const group = groups.find(g => g.elementIds.includes(elementId));
    if (group) {
      if (addToSelection) {
        setSelectedIds(prev => [...new Set([...prev, ...group.elementIds])]);
      } else {
        setSelectedIds(group.elementIds);
      }
    } else {
      if (addToSelection) {
        setSelectedIds(prev => prev.includes(elementId) ? prev.filter(id => id !== elementId) : [...prev, elementId]);
      } else {
        setSelectedIds([elementId]);
      }
    }
  };

  // Floor management functions
  const addFloor = () => {
    const newFloorNum = floors.length + 1;
    const floorNames = ['Planta Baja', 'Primer Piso', 'Segundo Piso', 'Tercer Piso', 'Cuarto Piso', 'Quinto Piso', 'Sexto Piso', 'Azotea'];
    const newFloor = {
      id: `floor_${Date.now()}`,
      name: floorNames[newFloorNum - 1] || `Piso ${newFloorNum}`,
      order: floors.length
    };
    setFloors(prev => [...prev, newFloor]);
    setActiveFloor(newFloor.id);
    toast.success(`${newFloor.name} agregado`);
  };

  const removeFloor = (floorId) => {
    if (floors.length <= 1) {
      toast.error('Debe haber al menos un piso');
      return;
    }
    // Remove elements on this floor
    setElements(prev => prev.filter(e => e.floor_id !== floorId));
    setFloors(prev => prev.filter(f => f.id !== floorId));
    if (activeFloor === floorId) {
      setActiveFloor(floors[0]?.id);
    }
    toast.success('Piso eliminado');
  };

  const renameFloor = (floorId, newName) => {
    setFloors(prev => prev.map(f => f.id === floorId ? { ...f, name: newName } : f));
  };

  // Filter elements by active floor
  const floorElements = elements.filter(e => (e.floor_id || 'floor_1') === activeFloor);
  const floorElementIds = floorElements.map(e => e.id);

  const toggleVisibility = (id) => {
    setElements(prev => prev.map(e => e.id === id ? { ...e, visible: !e.visible } : e));
  };

  const moveZ = (id, direction) => {
    const idx = elements.findIndex(e => e.id === id);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? Math.min(idx + 1, elements.length - 1) : Math.max(idx - 1, 0);
    if (newIdx === idx) return;
    const newElements = [...elements];
    const [removed] = newElements.splice(idx, 1);
    newElements.splice(newIdx, 0, removed);
    newElements.forEach((e, i) => e.zIndex = i);
    setElements(newElements);
  };

  // Handle style selection with subscription check
  const handleStyleSelect = (styleId, elementId = null) => {
    if (!canUseStyle(styleId)) {
      setShowUpgradeModal(true);
      return false;
    }
    if (elementId) {
      updateElement(elementId, { style: styleId });
    }
    return true;
  };

  const applyGlobalStyle = (styleId) => {
    if (!canUseStyle(styleId)) {
      setShowUpgradeModal(true);
      return;
    }
    setGlobalStyle(styleId);
    const newElements = elements.map(e => ({ ...e, style: styleId }));
    setElements(newElements);
    saveHistory(newElements);
    toast.success(`Estilo aplicado a todos`);
  };

  // Mouse handlers - FIXED: Check rotation handle FIRST before anything else
  const handleCanvasMouseDown = (e) => {
    if (e.button !== 0) return;
    const pos = getMousePos(e);
    
    // Drawing mode
    if (['rect', 'circle'].includes(activeTool)) {
      setIsDrawing(true);
      setDrawStart(pos);
      setCurrentDraw({ x: pos.x, y: pos.y, width: 0, height: 0 });
      return;
    }
    
    // IMPORTANT: Check rotation handle FIRST for selected element
    if (selectedIds.length === 1) {
      const selEl = elements.find(e => e.id === selectedIds[0]);
      if (selEl && !selEl.locked) {
        const p = selEl.position;
        const rotation = selEl.rotation || 0;
        const centerX = p.x + p.width / 2;
        const centerY = p.y + p.height / 2;
        
        // Rotation handle position (above element center, rotated around element center)
        // Handle is positioned at the TOP of the element, then rotated
        const rotHandleDistance = 35;
        const rotAngleRad = rotation * Math.PI / 180;
        // Unrotated position: directly above center
        const handleOffsetX = 0;
        const handleOffsetY = -(p.height / 2 + rotHandleDistance);
        // Rotate this offset around center
        const rotHandleX = centerX + handleOffsetX * Math.cos(rotAngleRad) - handleOffsetY * Math.sin(rotAngleRad);
        const rotHandleY = centerY + handleOffsetX * Math.sin(rotAngleRad) + handleOffsetY * Math.cos(rotAngleRad);
        
        const dist = Math.sqrt((pos.x - rotHandleX) ** 2 + (pos.y - rotHandleY) ** 2);
        if (dist < 15) {
          setDragState({ 
            type: 'rotate', 
            startPos: pos, 
            startRotation: rotation, 
            elementId: selEl.id, 
            center: { x: centerX, y: centerY } 
          });
          return;
        }
        
        // Check ALL 8 resize handles - account for rotation
        const hs = 14; // Handle size for hit detection
        const handles = [
          // Corners
          { id: 'nw', dx: -p.width/2, dy: -p.height/2, resizeMode: 'both' },
          { id: 'ne', dx: p.width/2, dy: -p.height/2, resizeMode: 'both' },
          { id: 'sw', dx: -p.width/2, dy: p.height/2, resizeMode: 'both' },
          { id: 'se', dx: p.width/2, dy: p.height/2, resizeMode: 'both' },
          // Sides
          { id: 'n', dx: 0, dy: -p.height/2, resizeMode: 'height' },
          { id: 's', dx: 0, dy: p.height/2, resizeMode: 'height' },
          { id: 'w', dx: -p.width/2, dy: 0, resizeMode: 'width' },
          { id: 'e', dx: p.width/2, dy: 0, resizeMode: 'width' },
        ];
        
        for (const handle of handles) {
          const rotatedX = centerX + handle.dx * Math.cos(rotAngleRad) - handle.dy * Math.sin(rotAngleRad);
          const rotatedY = centerY + handle.dx * Math.sin(rotAngleRad) + handle.dy * Math.cos(rotAngleRad);
          
          if (Math.abs(pos.x - rotatedX) < hs && Math.abs(pos.y - rotatedY) < hs) {
            setDragState({ 
              type: 'resize', 
              handle: handle.id,
              resizeMode: handle.resizeMode, 
              startPos: pos, 
              startElementPos: { ...p }, 
              elementId: selEl.id,
              startRotation: rotation,
              center: { x: centerX, y: centerY }
            });
            setGuides({ x: p.x, y: p.y, w: p.width, h: p.height });
            return;
          }
        }
      }
    }
    
    // Find element under cursor
    const sortedElements = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
    let clickedElement = null;
    
    for (const el of sortedElements) {
      if (!el.visible) continue;
      const p = el.position;
      const rotation = el.rotation || 0;
      const centerX = p.x + p.width / 2;
      const centerY = p.y + p.height / 2;
      
      // Transform click position to element's local coordinate system
      const rotAngleRad = -rotation * Math.PI / 180;
      const localX = (pos.x - centerX) * Math.cos(rotAngleRad) - (pos.y - centerY) * Math.sin(rotAngleRad) + centerX;
      const localY = (pos.x - centerX) * Math.sin(rotAngleRad) + (pos.y - centerY) * Math.cos(rotAngleRad) + centerY;
      
      if (localX >= p.x && localX <= p.x + p.width && localY >= p.y && localY <= p.y + p.height) {
        clickedElement = el;
        break;
      }
    }
    
    if (!clickedElement) {
      setSelectionBox({ startX: pos.x, startY: pos.y, endX: pos.x, endY: pos.y });
      if (!e.shiftKey) setSelectedIds([]);
      return;
    }
    
    if (clickedElement.locked) return;
    
    if (e.shiftKey) {
      setSelectedIds(prev => prev.includes(clickedElement.id) ? prev.filter(id => id !== clickedElement.id) : [...prev, clickedElement.id]);
    } else if (!selectedIds.includes(clickedElement.id)) {
      setSelectedIds([clickedElement.id]);
    }
    
    // Start drag
    const idsToMove = selectedIds.includes(clickedElement.id) ? selectedIds : [clickedElement.id];
    setDragState({ 
      type: 'drag', 
      startPos: pos, 
      elements: elements.filter(e => idsToMove.includes(e.id)).map(e => ({ id: e.id, startPos: { ...e.position } })) 
    });
    if (!selectedIds.includes(clickedElement.id)) setSelectedIds([clickedElement.id]);
  };

  const handleCanvasMouseMove = (e) => {
    const pos = getMousePos(e);
    
    if (isDrawing && drawStart) {
      const newDraw = { x: Math.min(drawStart.x, pos.x), y: Math.min(drawStart.y, pos.y), width: Math.abs(pos.x - drawStart.x), height: Math.abs(pos.y - drawStart.y) };
      setCurrentDraw(newDraw);
      setGuides({ x: newDraw.x, y: newDraw.y, w: newDraw.width, h: newDraw.height });
      return;
    }
    
    if (selectionBox) {
      setSelectionBox({ ...selectionBox, endX: pos.x, endY: pos.y });
      return;
    }
    
    const { type, startPos, handle, startElementPos, elementId, startRotation, center, elements: dragElements } = dragState;
    if (!type || !startPos) return;
    
    const dx = pos.x - startPos.x;
    const dy = pos.y - startPos.y;
    
    // Rotating
    if (type === 'rotate' && center) {
      const startAngle = Math.atan2(startPos.y - center.y, startPos.x - center.x);
      const currentAngle = Math.atan2(pos.y - center.y, pos.x - center.x);
      let newRotation = (startRotation || 0) + (currentAngle - startAngle) * (180 / Math.PI);
      if (snapToGrid) newRotation = Math.round(newRotation / 15) * 15;
      // Normalize to -180 to 180
      while (newRotation > 180) newRotation -= 360;
      while (newRotation < -180) newRotation += 360;
      updateElement(elementId, { rotation: newRotation });
      setGuides({ rotation: Math.round(newRotation) });
      return;
    }
    
    // Resizing
    if (type === 'resize' && startElementPos && handle) {
      let newPos = { ...startElementPos };
      const minSize = 10; // Reduced from 40 to allow thin walls
      
      if (handle.includes('e')) newPos.width = Math.max(minSize, snapValue(startElementPos.width + dx));
      if (handle.includes('w')) { const nw = Math.max(minSize, startElementPos.width - dx); newPos.x = snapValue(startElementPos.x + startElementPos.width - nw); newPos.width = nw; }
      if (handle.includes('s')) newPos.height = Math.max(minSize, snapValue(startElementPos.height + dy));
      if (handle.includes('n')) { const nh = Math.max(minSize, startElementPos.height - dy); newPos.y = snapValue(startElementPos.y + startElementPos.height - nh); newPos.height = nh; }
      
      updateElement(elementId, { position: newPos });
      setGuides({ x: newPos.x, y: newPos.y, w: newPos.width, h: newPos.height });
      return;
    }
    
    // Dragging - allow elements to reach all edges, even extend slightly outside
    if (type === 'drag' && dragElements) {
      dragElements.forEach(({ id, startPos: sp }) => {
        const el = elements.find(e => e.id === id);
        if (!el) return;
        const newX = snapValue(sp.x + dx);
        const newY = snapValue(sp.y + dy);
        // Allow elements to go to x=0, y=0 (exact edges)
        // Only prevent going more than 50% outside the canvas
        const minX = -el.position.width * 0.5;
        const maxX = canvasWidth - el.position.width * 0.5;
        const minY = -el.position.height * 0.5;
        const maxY = canvasHeight - el.position.height * 0.5;
        const finalX = Math.max(minX, Math.min(newX, maxX));
        const finalY = Math.max(minY, Math.min(newY, maxY));
        updateElement(id, { position: { ...el.position, x: finalX, y: finalY } });
      });
      const first = dragElements[0];
      if (first) {
        const el = elements.find(e => e.id === first.id);
        if (el) setGuides({ x: el.position.x, y: el.position.y, w: el.position.width, h: el.position.height });
      }
    }
  };

  const handleCanvasMouseUp = () => {
    if (isDrawing && currentDraw && currentDraw.width > 30 && currentDraw.height > 30) {
      const newElement = {
        id: `el_${Date.now()}`,
        type: 'section',
        name: `Sección ${elements.filter(e => e.type === 'section').length + 1}`,
        color: COLORS[elements.length % COLORS.length],
        capacity: 100,
        shape: activeTool === 'circle' ? 'circle' : 'rect',
        style: globalStyle,
        rotation: 0,
        position: { x: snapValue(currentDraw.x), y: snapValue(currentDraw.y), width: snapValue(currentDraw.width), height: snapValue(currentDraw.height) },
        locked: false,
        visible: true,
        zIndex: elements.length
      };
      const newElements = [...elements, newElement];
      setElements(newElements);
      saveHistory(newElements);
      setSelectedIds([newElement.id]);
    }
    
    if (selectionBox) {
      const minX = Math.min(selectionBox.startX, selectionBox.endX);
      const maxX = Math.max(selectionBox.startX, selectionBox.endX);
      const minY = Math.min(selectionBox.startY, selectionBox.endY);
      const maxY = Math.max(selectionBox.startY, selectionBox.endY);
      
      const selected = elements.filter(el => {
        if (!el.visible) return false;
        const p = el.position;
        return p.x < maxX && p.x + p.width > minX && p.y < maxY && p.y + p.height > minY;
      }).map(e => e.id);
      
      setSelectedIds(prev => [...new Set([...prev, ...selected])]);
      setSelectionBox(null);
    }
    
    if (dragState.type) saveHistory(elements);
    
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentDraw(null);
    setDragState({ type: null });
    setGuides({ x: null, y: null, w: null, h: null });
    
    if (activeTool !== 'select') setActiveTool('select');
  };

  const applyTemplate = (template) => {
    const newElements = template.default_sections.map((s, i) => ({
      ...s, 
      id: `el_${Date.now()}_${i}`, 
      type: s.type || 'section', 
      shape: s.shape || 'rect', 
      style: s.style || globalStyle, 
      rotation: s.rotation || 0, 
      locked: false, 
      visible: true, 
      zIndex: i
    }));
    setElements(newElements);
    saveHistory(newElements);
    setShowTemplates(false);
    toast.success(`Plantilla "${template.name}" aplicada`);
  };

  const handleSave = async () => {
    if (!venueName.trim()) { toast.error('El nombre del venue es requerido'); return; }
    setSaving(true);
    try {
      const venueData = { 
        name: venueName, 
        description: venueDescription, 
        address: venueAddress, 
        background_image: backgroundImage,
        background_preset: backgroundPreset,
        canvas_width: canvasWidth, 
        canvas_height: canvasHeight,
        canvas_shape: canvasShape,
        canvas_polygon: canvasShape === 'custom' ? (canvasPolygon || DEFAULT_CUSTOM_POLYGON) : null,
        grid_size: gridSize,
        floors: floors,
        sections: elements.map(el => ({
          id: el.id,
          type: el.type,
          name: el.name,
          color: el.color,
          capacity: el.capacity || 0,
          price: el.price,
          tier_id: el.tier_id,
          section_type: el.section_type || 'general',
          shape: el.shape,
          style: el.style,
          position: el.position,
          rotation: el.rotation || 0,
          locked: el.locked || false,
          visible: el.visible !== false,
          zIndex: el.zIndex || 0,
          floor_id: el.floor_id || 'floor_1'
        }))
      };
      const result = venue?.id ? await api.put(`/venues/${venue.id}`, venueData) : await api.post('/venues', venueData);
      toast.success(venue?.id ? 'Venue actualizado' : 'Venue creado');
      onSave?.(result.data.venue);
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  // Render guides
  const renderGuides = () => {
    if (!guides.x && guides.rotation === undefined) return null;
    
    if (guides.rotation !== undefined) {
      return (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 bg-violet-600 text-white px-4 py-2 rounded-full text-sm font-bold z-50 shadow-lg">
          {guides.rotation}°
        </div>
      );
    }
    
    return (
      <>
        <div className="absolute pointer-events-none bg-violet-500 z-40" style={{ left: 0, top: guides.y * zoom, width: canvasWidth * zoom, height: 1, opacity: 0.5 }} />
        <div className="absolute pointer-events-none bg-violet-500 z-40" style={{ left: 0, top: (guides.y + guides.h) * zoom, width: canvasWidth * zoom, height: 1, opacity: 0.5 }} />
        <div className="absolute pointer-events-none bg-violet-500 z-40" style={{ left: guides.x * zoom, top: 0, width: 1, height: canvasHeight * zoom, opacity: 0.5 }} />
        <div className="absolute pointer-events-none bg-violet-500 z-40" style={{ left: (guides.x + guides.w) * zoom, top: 0, width: 1, height: canvasHeight * zoom, opacity: 0.5 }} />
        <div className="absolute bg-zinc-800 text-white text-xs px-2 py-1 rounded z-50 pointer-events-none" style={{ left: (guides.x + guides.w/2) * zoom - 30, top: (guides.y - 25) * zoom }}>
          {Math.round(guides.w)} × {Math.round(guides.h)}
        </div>
      </>
    );
  };

  // Render handles - 8 handles (4 corners + 4 sides) with proper cursors
  const renderHandles = () => {
    if (selectedIds.length !== 1) return null;
    const el = elements.find(e => e.id === selectedIds[0]);
    if (!el || el.locked) return null;
    
    const p = el.position;
    const rotation = el.rotation || 0;
    const rad = rotation * Math.PI / 180;
    const centerX = (p.x + p.width / 2) * zoom;
    const centerY = (p.y + p.height / 2) * zoom;
    const cornerSize = 10;
    const sideSize = 8;
    
    // All 8 handles: 4 corners + 4 sides
    const handles = [
      // Corners - resize both width and height
      { id: 'nw', dx: -p.width/2, dy: -p.height/2, cursor: 'nwse-resize', type: 'corner', size: cornerSize },
      { id: 'ne', dx: p.width/2, dy: -p.height/2, cursor: 'nesw-resize', type: 'corner', size: cornerSize },
      { id: 'sw', dx: -p.width/2, dy: p.height/2, cursor: 'nesw-resize', type: 'corner', size: cornerSize },
      { id: 'se', dx: p.width/2, dy: p.height/2, cursor: 'nwse-resize', type: 'corner', size: cornerSize },
      // Sides - resize only width or height
      { id: 'n', dx: 0, dy: -p.height/2, cursor: 'ns-resize', type: 'side-v', size: sideSize, width: 24, height: 8 },
      { id: 's', dx: 0, dy: p.height/2, cursor: 'ns-resize', type: 'side-v', size: sideSize, width: 24, height: 8 },
      { id: 'w', dx: -p.width/2, dy: 0, cursor: 'ew-resize', type: 'side-h', size: sideSize, width: 8, height: 24 },
      { id: 'e', dx: p.width/2, dy: 0, cursor: 'ew-resize', type: 'side-h', size: sideSize, width: 8, height: 24 },
    ];
    
    // Rotation handle
    const rotHandleDistance = 35;
    const handleOffsetX = 0;
    const handleOffsetY = -(p.height / 2 + rotHandleDistance);
    const rotHandleX = centerX + (handleOffsetX * Math.cos(rad) - handleOffsetY * Math.sin(rad)) * zoom;
    const rotHandleY = centerY + (handleOffsetX * Math.sin(rad) + handleOffsetY * Math.cos(rad)) * zoom;
    
    // Line from center-top of element to rotation handle
    const lineStartY = -(p.height / 2);
    const lineStartX = 0;
    const rotatedLineStartX = centerX + (lineStartX * Math.cos(rad) - lineStartY * Math.sin(rad)) * zoom;
    const rotatedLineStartY = centerY + (lineStartX * Math.sin(rad) + lineStartY * Math.cos(rad)) * zoom;
    
    return (
      <>
        {/* Bounding box - rotates with the element */}
        <div 
          className="absolute border-2 border-violet-500 pointer-events-none z-40"
          style={{
            left: p.x * zoom,
            top: p.y * zoom,
            width: p.width * zoom,
            height: p.height * zoom,
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center'
          }}
        />
        
        {/* Rotation line - from element top-center to handle */}
        <svg 
          className="absolute top-0 left-0 pointer-events-none z-40" 
          style={{ width: '100%', height: '100%', overflow: 'visible' }}
        >
          <line 
            x1={rotatedLineStartX} 
            y1={rotatedLineStartY} 
            x2={rotHandleX} 
            y2={rotHandleY} 
            stroke="#8B5CF6" 
            strokeWidth="2"
          />
        </svg>
        
        {/* Rotation handle */}
        <div
          data-testid="rotation-handle"
          className="absolute bg-violet-500 border-2 border-white rounded-full z-50 cursor-grab flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          style={{
            left: rotHandleX - 9,
            top: rotHandleY - 9,
            width: 18,
            height: 18,
          }}
        >
          <RotateCw size={10} className="text-white" />
        </div>
        
        {/* All 8 resize handles */}
        {handles.map(handle => {
          const rotatedDx = handle.dx * Math.cos(rad) - handle.dy * Math.sin(rad);
          const rotatedDy = handle.dx * Math.sin(rad) + handle.dy * Math.cos(rad);
          
          const isCorner = handle.type === 'corner';
          const w = isCorner ? cornerSize : handle.width;
          const h = isCorner ? cornerSize : handle.height;
          
          // Adjust cursor based on rotation
          const getCursorForRotation = () => {
            const normalizedRot = ((rotation % 360) + 360) % 360;
            const rotIndex = Math.round(normalizedRot / 45) % 8;
            
            const cursorMap = {
              'nwse-resize': ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'],
              'nesw-resize': ['nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize'],
              'ns-resize': ['ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize'],
              'ew-resize': ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'],
            };
            return cursorMap[handle.cursor]?.[rotIndex] || handle.cursor;
          };
          
          return (
            <div
              key={handle.id}
              data-handle={handle.id}
              className={`absolute z-50 transition-all ${isCorner ? 'bg-white border-2 border-violet-500 hover:bg-violet-100' : 'bg-violet-500 border border-violet-300 hover:bg-violet-400'}`}
              style={{
                left: centerX + rotatedDx * zoom - w/2,
                top: centerY + rotatedDy * zoom - h/2,
                width: w,
                height: h,
                cursor: getCursorForRotation(),
                borderRadius: isCorner ? 2 : 3,
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }}
            />
          );
        })}
      </>
    );
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
        <div className="flex items-center gap-4">
          <Input value={venueName} onChange={(e) => setVenueName(e.target.value)} placeholder="Nombre del venue" className="bg-zinc-800 border-zinc-700 text-white text-lg font-bold w-72" />
          <span className="text-zinc-500 text-sm">{elements.filter(el => (el.floor_id || 'floor_1') === activeFloor).length} elementos{selectedIds.length > 0 && ` • ${selectedIds.length} seleccionado(s)`}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={copySelected} disabled={selectedIds.length === 0} className="text-zinc-400" title="Copiar (Ctrl+C)"><Clipboard size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={pasteClipboard} disabled={!clipboard} className="text-zinc-400" title="Pegar (Ctrl+V)"><ClipboardPaste size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={duplicateSelected} disabled={selectedIds.length === 0} className="text-zinc-400" title="Duplicar (Ctrl+D)"><Copy size={16} /></Button>
          <div className="w-px h-6 bg-zinc-700 mx-1" />
          <Button variant="ghost" size="sm" onClick={groupSelected} disabled={!canGroup} className="text-zinc-400 hover:text-violet-400" title="Agrupar (Ctrl+G)"><Group size={16} /></Button>
          <Button variant="ghost" size="sm" onClick={ungroupSelected} disabled={!canUngroup} className="text-zinc-400 hover:text-violet-400" title="Desagrupar (Ctrl+Shift+G)"><Ungroup size={16} /></Button>
          <div className="w-px h-6 bg-zinc-700 mx-1" />
          <Button variant="ghost" size="sm" onClick={deleteSelected} disabled={selectedIds.length === 0} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" title="Eliminar (Delete)"><Trash2 size={16} /></Button>
          <div className="w-px h-6 bg-zinc-700 mx-1" />
          <Button variant="ghost" size="sm" onClick={undo} disabled={historyIndex <= 0} className="text-zinc-400"><RotateCcw size={16} /></Button>
          <Button variant="ghost" onClick={onCancel} className="text-zinc-400"><X size={16} className="mr-2" /> Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black">
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Guardar
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Toolbar */}
        <div className="w-14 bg-zinc-900 border-r border-zinc-800 flex flex-col items-center py-3 gap-1">
          <Button variant={activeTool === 'select' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTool('select')} className={`w-10 h-10 p-0 ${activeTool === 'select' ? 'bg-violet-600' : 'text-zinc-400'}`} title="Seleccionar"><MousePointer size={18} /></Button>
          <div className="h-px w-8 bg-zinc-800 my-2" />
          <Button variant={activeTool === 'rect' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTool('rect')} className={`w-10 h-10 p-0 ${activeTool === 'rect' ? 'bg-violet-600' : 'text-zinc-400'}`} title="Dibujar Sección"><Square size={18} /></Button>
          <Button variant={activeTool === 'circle' ? 'default' : 'ghost'} size="sm" onClick={() => setActiveTool('circle')} className={`w-10 h-10 p-0 ${activeTool === 'circle' ? 'bg-violet-600' : 'text-zinc-400'}`} title="Dibujar Círculo"><Circle size={18} /></Button>
          <div className="h-px w-8 bg-zinc-800 my-2" />
          <Button variant="ghost" size="sm" onClick={() => setShowElementPicker(true)} className="w-10 h-10 p-0 text-zinc-400 hover:bg-violet-600/20" title="Agregar Elemento"><Plus size={18} /></Button>
          <Button variant="ghost" size="sm" onClick={() => setShowTemplates(true)} className="w-10 h-10 p-0 text-zinc-400" title="Plantillas"><Layout size={18} /></Button>
          <div className="flex-1" />
          <Button variant={showGrid ? 'default' : 'ghost'} size="sm" onClick={() => setShowGrid(!showGrid)} className={`w-10 h-10 p-0 ${showGrid ? 'bg-zinc-700' : 'text-zinc-400'}`} title="Cuadrícula">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="0" y1="6" x2="18" y2="6"/><line x1="0" y1="12" x2="18" y2="12"/><line x1="6" y1="0" x2="6" y2="18"/><line x1="12" y1="0" x2="12" y2="18"/></svg>
          </Button>
          <div className="h-px w-8 bg-zinc-800 my-2" />
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.min(z + 0.1, 2))} className="w-10 h-10 p-0 text-zinc-400"><ZoomIn size={16} /></Button>
          <span className="text-xs text-zinc-500">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setZoom(z => Math.max(z - 0.1, 0.5))} className="w-10 h-10 p-0 text-zinc-400"><ZoomOut size={16} /></Button>
        </div>

        {/* Canvas Area with Floor Tabs */}
        <div className="flex-1 flex flex-col overflow-hidden bg-zinc-950">
          {/* Floor Tabs */}
          <div className="flex items-center gap-2 px-8 py-3 bg-zinc-900/50 border-b border-zinc-800">
            <span className="text-xs text-zinc-500 mr-2">PISOS:</span>
            {floors.map((floor, idx) => (
              <button
                key={floor.id}
                onClick={() => setActiveFloor(floor.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${activeFloor === floor.id ? 'bg-violet-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
              >
                <span>{floor.name}</span>
                <span className="text-xs opacity-60">({elements.filter(e => (e.floor_id || 'floor_1') === floor.id).length})</span>
              </button>
            ))}
            <button
              onClick={addFloor}
              className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white transition-all"
            >
              <Plus size={14} />
              <span>Agregar Piso</span>
            </button>
            {floors.length > 1 && (
              <button
                onClick={() => removeFloor(activeFloor)}
                className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs bg-zinc-800 text-red-400 hover:bg-red-500/20 transition-all"
                title="Eliminar piso actual"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          
          {/* Canvas */}
          <div className="flex-1 overflow-auto p-8">
            <div
              className="relative mx-auto"
              style={{
                width: canvasWidth * zoom,
                height: canvasShape === 'circle' ? canvasWidth * zoom : canvasHeight * zoom,
              }}
            >
            <div 
              ref={canvasRef}
              data-testid="venue-canvas"
              className="absolute inset-0 border border-zinc-800 shadow-2xl select-none overflow-hidden"
              style={{ 
                backgroundColor: BACKGROUND_PRESETS.find(b => b.id === backgroundPreset)?.color || '#000000',
                backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none', 
                backgroundSize: 'cover', 
                cursor: activeTool !== 'select' ? 'crosshair' : dragState.type === 'drag' ? 'grabbing' : dragState.type === 'rotate' ? 'grabbing' : 'default',
                borderRadius: getCanvasBorderRadius(canvasShape),
                clipPath: getCanvasClipPath(canvasShape, canvasPolygon)
              }}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
            >
            {/* Premium Background Patterns */}
            
            {/* Minimal Dots - Very subtle premium dots */}
            {backgroundPreset === 'minimal-dots' && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.02) 1px, transparent 1px)`,
                  backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
                }} />
              </div>
            )}
            
            {/* Neo Grid - Modern futuristic grid */}
            {backgroundPreset === 'neo-grid' && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Subtle glow in center */}
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at 50% 50%, rgba(139,92,246,0.03) 0%, transparent 60%)'
                }} />
                {/* Main grid */}
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(139,92,246,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.08) 1px, transparent 1px)`, 
                  backgroundSize: `${gridSize * 4 * zoom}px ${gridSize * 4 * zoom}px`
                }} />
                {/* Micro grid */}
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(139,92,246,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.03) 1px, transparent 1px)`, 
                  backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
                }} />
              </div>
            )}
            
            {/* Blueprint Pro - Professional architectural */}
            {backgroundPreset === 'blueprint-pro' && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(59,130,246,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(59,130,246,0.12) 1px, transparent 1px)`, 
                  backgroundSize: `${gridSize * 5 * zoom}px ${gridSize * 5 * zoom}px`
                }} />
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(59,130,246,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(59,130,246,0.04) 1px, transparent 1px)`, 
                  backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
                }} />
                {/* Subtle noise texture */}
                <div className="absolute inset-0 opacity-5" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
                }} />
              </div>
            )}
            
            {/* Cyber Dark - Cyberpunk aesthetic */}
            {backgroundPreset === 'cyber-dark' && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {/* Ambient glow spots */}
                <div className="absolute w-96 h-96 rounded-full blur-[150px]" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.08), transparent 70%)', top: '-10%', right: '-5%' }} />
                <div className="absolute w-80 h-80 rounded-full blur-[120px]" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.06), transparent 70%)', bottom: '-5%', left: '10%' }} />
                {/* Grid */}
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(236,72,153,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.05) 1px, transparent 1px)`, 
                  backgroundSize: `${gridSize * 3 * zoom}px ${gridSize * 3 * zoom}px`
                }} />
                {/* Scan lines */}
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.15) 2px, rgba(0,0,0,0.15) 4px)`,
                }} />
              </div>
            )}
            
            {/* Gradient Violet - Ambient violet lighting */}
            {backgroundPreset === 'gradient-violet' && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at 30% 20%, rgba(139,92,246,0.12) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(168,85,247,0.08) 0%, transparent 50%)'
                }} />
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.015) 1px, transparent 1px)`,
                  backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
                }} />
              </div>
            )}
            
            {/* Gradient Warm - Premium warm tones */}
            {backgroundPreset === 'gradient-warm' && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at 20% 30%, rgba(251,146,60,0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(239,68,68,0.05) 0%, transparent 50%)'
                }} />
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.015) 1px, transparent 1px)`,
                  backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px`
                }} />
              </div>
            )}
            
            {/* Carbon Fiber - Premium carbon texture */}
            {backgroundPreset === 'carbon-fiber' && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(-45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 4px)`,
                  backgroundSize: `${6 * zoom}px ${6 * zoom}px`
                }} />
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.02) 1px, transparent 1px)`,
                  backgroundSize: `${gridSize * 2 * zoom}px ${gridSize * 2 * zoom}px`
                }} />
              </div>
            )}
            
            {/* Hexagon Pattern - Modern hexagonal grid */}
            {backgroundPreset === 'hex-pattern' && (
              <div className="absolute inset-0 pointer-events-none">
                <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="hex" x="0" y="0" width={50 * zoom} height={43.3 * zoom} patternUnits="userSpaceOnUse">
                      <polygon points={`${25*zoom},0 ${50*zoom},${12.5*zoom} ${50*zoom},${37.5*zoom} ${25*zoom},${50*zoom} 0,${37.5*zoom} 0,${12.5*zoom}`} fill="none" stroke="rgba(139,92,246,0.06)" strokeWidth="1"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#hex)" />
                </svg>
              </div>
            )}
            
            {/* Studio Floor - Professional studio look */}
            {backgroundPreset === 'studio-floor' && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Subtle vignette */}
                <div className="absolute inset-0" style={{
                  background: 'radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(0,0,0,0.3) 100%)'
                }} />
                {/* Grid */}
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.04) 1px, transparent 1px)`,
                  backgroundSize: `${gridSize * 4 * zoom}px ${gridSize * 4 * zoom}px`
                }} />
                <div className="absolute inset-0" style={{ 
                  backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.08) 2px, transparent 2px), linear-gradient(to bottom, rgba(255,255,255,0.08) 2px, transparent 2px)`,
                  backgroundSize: `${gridSize * 8 * zoom}px ${gridSize * 8 * zoom}px`
                }} />
              </div>
            )}
            
            {/* Default grid for 'none' preset when showGrid is on */}
            {showGrid && backgroundPreset === 'none' && (
              <div className="absolute inset-0 pointer-events-none" style={{ 
                backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)`, 
                backgroundSize: `${gridSize * zoom}px ${gridSize * zoom}px` 
              }} />
            )}

            {/* Elements - filtered by active floor */}
            {[...elements]
              .filter(el => (el.floor_id || 'floor_1') === activeFloor)
              .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
              .map(el => (
              <div
                key={el.id}
                className="absolute"
                style={{
                  left: el.position.x * zoom,
                  top: el.position.y * zoom,
                  width: el.position.width * zoom,
                  height: el.position.height * zoom,
                  opacity: el.visible ? 1 : 0.3,
                  cursor: el.locked ? 'not-allowed' : 'grab',
                  transform: `rotate(${el.rotation || 0}deg)`,
                  transformOrigin: 'center center',
                  pointerEvents: el.visible ? 'auto' : 'none'
                }}
              >
                <ElementShape element={el} width={el.position.width * zoom} height={el.position.height * zoom} isSelected={selectedIds.includes(el.id)} />
                {el.locked && <div className="absolute top-1 right-1 bg-black/50 rounded p-0.5"><Lock size={10} className="text-zinc-400" /></div>}
              </div>
            ))}

            {renderHandles()}
            {renderGuides()}

            {selectionBox && (
              <div className="absolute border-2 border-violet-500 bg-violet-500/10 pointer-events-none z-50" style={{
                left: Math.min(selectionBox.startX, selectionBox.endX) * zoom,
                top: Math.min(selectionBox.startY, selectionBox.endY) * zoom,
                width: Math.abs(selectionBox.endX - selectionBox.startX) * zoom,
                height: Math.abs(selectionBox.endY - selectionBox.startY) * zoom
              }} />
            )}

            {isDrawing && currentDraw && currentDraw.width > 10 && (
              <div className="absolute border-2 border-dashed border-[#D4AF37] bg-[#D4AF37]/10 pointer-events-none" style={{ left: currentDraw.x * zoom, top: currentDraw.y * zoom, width: currentDraw.width * zoom, height: currentDraw.height * zoom, borderRadius: activeTool === 'circle' ? '50%' : '8px' }} />
            )}
          </div>
            {/* Polygon vertex-edit overlay (sibling of canvas to avoid clipping) */}
            {canvasShape === 'custom' && editingPolygon && (
              <PolygonOverlay
                polygon={canvasPolygon || DEFAULT_CUSTOM_POLYGON}
                onChange={setCanvasPolygon}
                width={canvasWidth * zoom}
                height={(canvasShape === 'circle' ? canvasWidth : canvasHeight) * zoom}
              />
            )}
            </div>
        </div>
        </div>

        {/* Right Panel */}
        <div className="w-80 bg-zinc-900 border-l border-zinc-800 flex flex-col">
          <div className="flex border-b border-zinc-800">
            <button onClick={() => setRightPanelTab('properties')} className={`flex-1 py-3 text-sm font-medium ${rightPanelTab === 'properties' ? 'text-white border-b-2 border-violet-500' : 'text-zinc-500'}`}>Propiedades</button>
            <button onClick={() => setRightPanelTab('layers')} className={`flex-1 py-3 text-sm font-medium ${rightPanelTab === 'layers' ? 'text-white border-b-2 border-violet-500' : 'text-zinc-500'}`}>Capas ({elements.filter(el => (el.floor_id || 'floor_1') === activeFloor).length})</button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {rightPanelTab === 'properties' ? (
              selectedElement ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">{ELEMENT_TYPES[selectedElement.type]?.label}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => updateElement(selectedIds[0], { locked: !selectedElement.locked })} className="h-7 w-7 p-0">{selectedElement.locked ? <Lock size={14} /> : <Unlock size={14} />}</Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleVisibility(selectedIds[0])} className="h-7 w-7 p-0">{selectedElement.visible ? <Eye size={14} /> : <EyeOff size={14} />}</Button>
                      <Button variant="ghost" size="sm" onClick={duplicateSelected} className="h-7 w-7 p-0"><Copy size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={deleteSelected} className="h-7 w-7 p-0 text-red-400"><Trash2 size={14} /></Button>
                    </div>
                  </div>

                  <div><Label className="text-zinc-400 text-xs">Nombre</Label><Input value={selectedElement.name} onChange={(e) => updateElement(selectedIds[0], { name: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>

                  <div><Label className="text-zinc-400 text-xs">Forma</Label>
                    <div className="grid grid-cols-5 gap-1.5 mt-2">
                      {SHAPES.map(s => (<button key={s.id} onClick={() => updateElement(selectedIds[0], { shape: s.id })} className={`w-10 h-10 rounded-lg border flex items-center justify-center ${selectedElement.shape === s.id ? 'border-violet-500 bg-violet-500/20' : 'border-zinc-700 hover:border-zinc-600'}`} title={s.label}><s.icon size={16} className="text-zinc-300" /></button>))}
                    </div>
                  </div>

                  <div><Label className="text-zinc-400 text-xs">Estilo Visual</Label>
                    <Select 
                      value={selectedElement.style || 'modern'} 
                      onValueChange={(v) => {
                        if (!canUseStyle(v)) {
                          setShowUpgradeModal(true);
                          return;
                        }
                        updateElement(selectedIds[0], { style: v });
                      }}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {STYLES.map(s => {
                          const isLocked = !canUseStyle(s.id);
                          return (
                            <SelectItem 
                              key={s.id} 
                              value={s.id} 
                              className={`text-white focus:bg-zinc-700 ${isLocked ? 'opacity-60' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                {isLocked && <Crown size={12} className="text-[#D4AF37]" />}
                                <span>{s.label}</span>
                                {isLocked && <span className="text-xs text-[#D4AF37]">Premium</span>}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div><Label className="text-zinc-400 text-xs">Color</Label>
                    <div className="flex flex-wrap gap-2 mt-2">{COLORS.map(c => (<button key={c} onClick={() => updateElement(selectedIds[0], { color: c })} className={`w-6 h-6 rounded-lg border-2 ${selectedElement.color === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />))}</div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between"><Label className="text-zinc-400 text-xs">Rotación</Label><span className="text-xs text-zinc-500">{Math.round(selectedElement.rotation || 0)}°</span></div>
                    <div className="flex items-center gap-2 mt-2">
                      <Slider value={[selectedElement.rotation || 0]} onValueChange={([v]) => updateElement(selectedIds[0], { rotation: v })} min={-180} max={180} step={5} className="flex-1" />
                      <Button variant="ghost" size="sm" onClick={() => updateElement(selectedIds[0], { rotation: 0 })} className="h-7 w-7 p-0 text-zinc-400"><RotateCcw size={12} /></Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-zinc-400 text-xs">Ancho</Label><Input type="number" value={Math.round(selectedElement.position.width)} onChange={(e) => updateElement(selectedIds[0], { position: { ...selectedElement.position, width: parseInt(e.target.value) || 50 } })} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                    <div><Label className="text-zinc-400 text-xs">Alto</Label><Input type="number" value={Math.round(selectedElement.position.height)} onChange={(e) => updateElement(selectedIds[0], { position: { ...selectedElement.position, height: parseInt(e.target.value) || 50 } })} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                  </div>

                  {ELEMENT_TYPES[selectedElement.type]?.hasCapacity && (
                    <div><Label className="text-zinc-400 text-xs">Capacidad</Label><Input type="number" value={selectedElement.capacity || 0} onChange={(e) => updateElement(selectedIds[0], { capacity: parseInt(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                  )}
                </div>
              ) : selectedIds.length > 1 ? (
                <div className="space-y-4">
                  <h3 className="font-bold text-white">{selectedIds.length} elementos seleccionados</h3>
                  <p className="text-sm text-zinc-500">Usa los botones en la barra superior para copiar, duplicar o eliminar los elementos seleccionados.</p>
                  <div className="flex gap-2">
                    <Button onClick={duplicateSelected} className="flex-1"><Copy size={14} className="mr-2" /> Duplicar</Button>
                    <Button variant="destructive" onClick={deleteSelected} className="flex-1"><Trash2 size={14} className="mr-2" /> Eliminar</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="font-bold text-white">Configuración</h3>
                  <div><Label className="text-zinc-400 text-xs flex items-center gap-2"><Palette size={12} />Estilo Global</Label>
                    <Select value={globalStyle} onValueChange={applyGlobalStyle}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {STYLES.map(s => {
                          const isLocked = !canUseStyle(s.id);
                          return (
                            <SelectItem 
                              key={s.id} 
                              value={s.id} 
                              className={`text-white focus:bg-zinc-700 ${isLocked ? 'opacity-60' : ''}`}
                            >
                              <div className="flex items-center gap-2">
                                {isLocked && <Crown size={12} className="text-[#D4AF37]" />}
                                <span>{s.label}</span>
                                {isLocked && <span className="text-xs text-[#D4AF37]">Premium</span>}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-400 text-xs flex items-center gap-2 mb-2">
                      <Layout size={12} />Fondo del Mapa
                    </Label>
                    <div className="grid grid-cols-5 gap-2">
                      {BACKGROUND_PRESETS.map(bg => {
                        const bgLocked = !canUseBackground(bg.id);
                        return (
                          <button
                            key={bg.id}
                            data-testid={`bg-preset-${bg.id}`}
                            data-locked={bgLocked ? 'true' : 'false'}
                            onClick={() => {
                              if (bgLocked) {
                                setShowUpgradeModal(true);
                                return;
                              }
                              setBackgroundPreset(bg.id);
                            }}
                            className={`relative h-9 rounded-lg border-2 transition-all overflow-hidden ${backgroundPreset === bg.id ? 'border-violet-500 ring-2 ring-violet-500/30' : 'border-zinc-700 hover:border-zinc-600'} ${bgLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            style={{ backgroundColor: bg.color }}
                            title={bgLocked ? `${bg.label} — Super Productora` : bg.label}
                          >
                            {bg.id === 'minimal-dots' && <div className="w-full h-full" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />}
                            {bg.id === 'neo-grid' && <div className="w-full h-full" style={{ backgroundImage: 'linear-gradient(to right, rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(to bottom, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '8px 8px' }} />}
                            {bg.id === 'blueprint-pro' && <div className="w-full h-full" style={{ backgroundImage: 'linear-gradient(to right, rgba(59,130,246,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(59,130,246,0.4) 1px, transparent 1px)', backgroundSize: '6px 6px' }} />}
                            {bg.id === 'cyber-dark' && <div className="w-full h-full relative overflow-hidden"><div className="absolute w-6 h-6 rounded-full bg-violet-500/30 blur-md" style={{top:'-10%',right:'-10%'}}/><div className="absolute w-4 h-4 rounded-full bg-pink-500/20 blur-sm" style={{bottom:'0',left:'10%'}}/></div>}
                            {bg.id === 'gradient-violet' && <div className="w-full h-full" style={{ background: 'radial-gradient(ellipse at 30% 30%, rgba(139,92,246,0.3) 0%, transparent 70%)' }} />}
                            {bg.id === 'gradient-warm' && <div className="w-full h-full" style={{ background: 'radial-gradient(ellipse at 30% 30%, rgba(251,146,60,0.25) 0%, transparent 70%)' }} />}
                            {bg.id === 'carbon-fiber' && <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 3px)' }} />}
                            {bg.id === 'hex-pattern' && <div className="w-full h-full flex items-center justify-center"><div className="w-4 h-4 border border-violet-500/40" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }} /></div>}
                            {bg.id === 'studio-floor' && <div className="w-full h-full" style={{ backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '8px 8px', boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)' }} />}
                            {bgLocked && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                                <Crown size={12} className="text-[#D4AF37]" />
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-zinc-500 mt-2">{BACKGROUND_PRESETS.find(b => b.id === backgroundPreset)?.label}</p>
                  </div>
                  
                  <div><Label className="text-zinc-400 text-xs">Descripción</Label><Textarea value={venueDescription} onChange={(e) => setVenueDescription(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white mt-1 min-h-[60px]" /></div>
                  <div><Label className="text-zinc-400 text-xs">Dirección</Label><Input value={venueAddress} onChange={(e) => setVenueAddress(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-zinc-400 text-xs">Ancho Canvas</Label><Input type="number" value={canvasWidth} onChange={(e) => setCanvasWidth(parseInt(e.target.value) || 800)} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                    <div><Label className="text-zinc-400 text-xs">Alto Canvas</Label><Input type="number" value={canvasHeight} onChange={(e) => setCanvasHeight(parseInt(e.target.value) || 600)} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
                  </div>
                  
                  {/* Canvas Shape */}
                  <div>
                    <Label className="text-zinc-400 text-xs mb-2 block flex items-center gap-1.5">
                      <Anchor size={11} className="text-violet-400" />
                      Forma del Venue
                    </Label>
                    <div className="grid grid-cols-4 gap-2" data-testid="canvas-shape-picker">
                      {CANVAS_SHAPE_OPTIONS.map(shape => {
                        const shapeLocked = !canUseCanvasShape(shape.id);
                        return (
                        <button
                          key={shape.id}
                          data-testid={`canvas-shape-${shape.id}`}
                          data-locked={shapeLocked ? 'true' : 'false'}
                          onClick={() => {
                            if (shapeLocked) {
                              setShowUpgradeModal(true);
                              return;
                            }
                            const prevShape = canvasShape;
                            setCanvasShape(shape.id);
                            // Seed custom polygon from the preset the user is leaving (for easy tweaking)
                            if (shape.id === 'custom' && !canvasPolygon) {
                              const seed = SHAPE_POLYGONS[prevShape] || DEFAULT_CUSTOM_POLYGON;
                              setCanvasPolygon(seed.map(p => ({ ...p })));
                              setEditingPolygon(true);
                            }
                            if (shape.id !== 'custom') {
                              setEditingPolygon(false);
                            }
                            // Auto-apply nautical template when picking a boat shape on an empty floor
                            if (hasNauticalTemplate(shape.id) && prevShape !== shape.id) {
                              const floorEls = elements.filter(e => (e.floor_id || activeFloor) === activeFloor);
                              if (floorEls.length === 0) {
                                const tplEls = getTemplateElements(shape.id, canvasWidth, canvasHeight, activeFloor, globalStyle);
                                const next = [...elements, ...tplEls];
                                setElements(next);
                                saveHistory(next);
                                toast.success(`${TEMPLATE_LABELS[shape.id]} cargada — movelos a gusto`);
                              }
                            }
                          }}
                          className={`p-2 rounded-lg border text-center transition-all relative ${canvasShape === shape.id ? 'border-violet-500 bg-violet-500/10' : 'border-zinc-700 hover:border-zinc-600'} ${shapeLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={shapeLocked ? `${shape.label} — Super Productora` : shape.label}
                        >
                          <div className="text-lg text-zinc-300">{shape.icon}</div>
                          <div className="text-[9px] text-zinc-500">{shape.label}</div>
                          {shape.category === 'nautical' && !shapeLocked && (
                            <span className="absolute -top-1 -right-1 bg-cyan-500 text-[7px] font-bold px-1 rounded text-white">⚓</span>
                          )}
                          {shapeLocked && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
                              <Crown size={12} className="text-[#D4AF37]" />
                            </div>
                          )}
                        </button>
                        );
                      })}
                    </div>
                    {/* Nautical template loader (manual) — visible when current shape is nautical */}
                    {hasNauticalTemplate(canvasShape) && (
                      <button
                        data-testid={`load-template-${canvasShape}`}
                        onClick={() => {
                          const tplEls = getTemplateElements(canvasShape, canvasWidth, canvasHeight, activeFloor, globalStyle);
                          const next = [...elements, ...tplEls];
                          setElements(next);
                          saveHistory(next);
                          toast.success(`${TEMPLATE_LABELS[canvasShape]} agregada`);
                        }}
                        className="mt-3 w-full px-3 py-2 rounded-lg text-[11px] font-semibold uppercase tracking-wider bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-200 transition-all flex items-center justify-center gap-1.5"
                      >
                        <Anchor size={11} />
                        Cargar plantilla náutica
                      </button>
                    )}
                    {canvasShape === 'custom' && (
                      <div className="mt-3 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-[11px] text-violet-200 font-medium flex items-center gap-1.5">
                            <Pencil size={11} />
                            Polígono custom
                          </div>
                          <span className="text-[10px] text-zinc-500">{(canvasPolygon || []).length} vértices</span>
                        </div>
                        <button
                          data-testid="toggle-polygon-edit"
                          onClick={() => setEditingPolygon(v => !v)}
                          className={`w-full px-3 py-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 ${editingPolygon ? 'bg-violet-500 text-white' : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'}`}
                        >
                          {editingPolygon ? <><Check size={12} /> Listo</> : <><Pencil size={12} /> Editar vértices</>}
                        </button>
                        <div className="text-[10px] text-zinc-500 leading-relaxed">
                          {editingPolygon
                            ? 'Arrastrá los puntos para mover · Click en una arista para agregar · Doble-click en un punto para borrar'
                            : 'Activá editar para mover los vértices del contorno.'}
                        </div>
                        <button
                          data-testid="reset-polygon"
                          onClick={() => {
                            setCanvasPolygon(DEFAULT_CUSTOM_POLYGON.map(p => ({ ...p })));
                          }}
                          className="w-full px-3 py-1.5 rounded-md text-[10px] text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-500 transition-all"
                        >
                          Resetear contorno
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Grid Settings */}
                  <div className="space-y-2 pt-2 border-t border-zinc-800">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Mostrar cuadrícula</span>
                      <Switch checked={showGrid} onCheckedChange={setShowGrid} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-zinc-400">Ajustar a cuadrícula</span>
                      <Switch checked={snapToGrid} onCheckedChange={setSnapToGrid} />
                    </div>
                    {showGrid && (
                      <div>
                        <Label className="text-zinc-400 text-xs flex items-center justify-between">
                          Tamaño cuadrícula
                          <span className="text-violet-400">{gridSize}px</span>
                        </Label>
                        <input 
                          type="range" 
                          min="10" 
                          max="50" 
                          step="5"
                          value={gridSize} 
                          onChange={(e) => setGridSize(parseInt(e.target.value))}
                          className="w-full mt-1 accent-violet-500"
                        />
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-zinc-500 pt-2">Arrastra para seleccionar múltiples elementos. Shift+clic para agregar a la selección.</p>
                </div>
              )
            ) : (
              <div className="space-y-1">
                {/* Show floor name */}
                <div className="text-xs text-zinc-500 px-2 py-1 mb-2 border-b border-zinc-800">
                  {floors.find(f => f.id === activeFloor)?.name || 'Planta Baja'}
                </div>
                {elements.filter(el => (el.floor_id || 'floor_1') === activeFloor).length === 0 ? <p className="text-center text-zinc-500 py-8">No hay elementos en este piso</p> : (
                  [...elements].filter(el => (el.floor_id || 'floor_1') === activeFloor).reverse().map((el) => {
                    const Icon = ELEMENT_TYPES[el.type]?.icon || Square;
                    return (
                      <div key={el.id} onMouseDown={(e) => {
                        e.stopPropagation();
                        if (e.shiftKey) {
                          setSelectedIds(prev => prev.includes(el.id) ? prev.filter(id => id !== el.id) : [...prev, el.id]);
                        } else {
                          setSelectedIds([el.id]);
                        }
                      }} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer ${selectedIds.includes(el.id) ? 'bg-violet-600/20 border border-violet-500/50' : 'hover:bg-zinc-800/50'}`}>
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: el.color }} />
                        {typeof Icon === 'function' ? <Icon size={14} className="text-zinc-400" /> : <Square size={14} className="text-zinc-400" />}
                        <span className={`text-sm flex-1 truncate ${el.visible ? 'text-white' : 'text-zinc-500 line-through'}`}>{el.name}</span>
                        <span className="text-xs text-zinc-500">{Math.round(el.rotation || 0)}°</span>
                        <div className="flex items-center gap-0.5">
                          {el.locked && <Lock size={10} className="text-zinc-500" />}
                          <button onClick={(e) => { e.stopPropagation(); toggleVisibility(el.id); }} className="p-1 hover:bg-zinc-700 rounded" title={el.visible ? 'Ocultar' : 'Mostrar'}>
                            {el.visible ? <Eye size={12} className="text-zinc-400" /> : <EyeOff size={12} className="text-zinc-500" />}
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); setSelectedIds([el.id]); duplicateSelected(); }} className="p-1 hover:bg-zinc-700 rounded" title="Duplicar">
                            <Copy size={12} className="text-zinc-400" />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); moveZ(el.id, 'up'); }} className="p-0.5 hover:bg-zinc-700 rounded"><ChevronUp size={12} className="text-zinc-400" /></button>
                          <button onClick={(e) => { e.stopPropagation(); moveZ(el.id, 'down'); }} className="p-0.5 hover:bg-zinc-700 rounded"><ChevronDown size={12} className="text-zinc-400" /></button>
                          <button onClick={(e) => { e.stopPropagation(); const newEls = elements.filter(x => x.id !== el.id); setElements(newEls); saveHistory(newEls); if (selectedIds.includes(el.id)) setSelectedIds(selectedIds.filter(id => id !== el.id)); }} className="p-1 hover:bg-red-500/20 rounded" title="Eliminar">
                            <Trash2 size={12} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={showElementPicker} onOpenChange={setShowElementPicker}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl">
          <DialogHeader><DialogTitle className="text-white">Agregar Elemento</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4 max-h-[65vh] overflow-y-auto">
            {/* Main Elements */}
            <div>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Elementos Principales</h3>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(ELEMENT_TYPES).filter(([_, config]) => config.category === 'main').map(([type, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <button key={type} onClick={() => addElement(type)} className="flex flex-col items-center gap-2 p-3 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800 hover:border-violet-500/50 transition-all">
                      <div className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center">
                        <IconComponent className="text-zinc-300" size={20} />
                      </div>
                      <span className="text-xs font-medium text-white text-center">{config.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Structure/Drawing Elements */}
            <div>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Estructura y Dibujo</h3>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(ELEMENT_TYPES).filter(([_, config]) => config.category === 'structure').map(([type, config]) => (
                  <button key={type} onClick={() => addElement(type)} className="flex flex-col items-center gap-1 p-2 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800 hover:border-violet-500/50 transition-all">
                    <div className="w-12 h-6 flex items-center justify-center" style={{ color: config.color }}>
                      {type === 'wall' && <div className="w-10 h-1.5 rounded-sm" style={{ backgroundColor: config.color }} />}
                      {type === 'wall_thick' && <div className="w-8 h-3 rounded-sm" style={{ backgroundColor: config.color }} />}
                      {type === 'wall_thin' && <div className="w-10 h-0.5" style={{ backgroundColor: config.color }} />}
                      {type === 'wall_dashed' && <div className="w-10 border-t-2 border-dashed" style={{ borderColor: config.color }} />}
                      {type === 'corner_wall' && <div className="w-4 h-4 border-l-2 border-b-2" style={{ borderColor: config.color }} />}
                      {type === 'pillar' && <div className="w-4 h-4 rounded-full" style={{ backgroundColor: config.color }} />}
                      {type === 'circle_deco' && <div className="w-5 h-5 rounded-full border border-dashed" style={{ borderColor: config.color }} />}
                      {type === 'rect_deco' && <div className="w-6 h-4 border border-dashed rounded-sm" style={{ borderColor: config.color }} />}
                      {type === 'area' && <div className="w-7 h-4 rounded opacity-40" style={{ backgroundColor: config.color }} />}
                    </div>
                    <span className="text-[10px] text-zinc-400 text-center leading-tight">{config.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Guide Elements */}
            <div>
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">Señalización y Guías</h3>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(ELEMENT_TYPES).filter(([_, config]) => config.category === 'guide').map(([type, config]) => (
                  <button key={type} onClick={() => addElement(type)} className="flex flex-col items-center gap-1 p-2 rounded-lg border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800 hover:border-violet-500/50 transition-all">
                    <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${config.color}20`, border: `1px solid ${config.color}40` }}>
                      {type === 'exit' && <span style={{ color: config.color }} className="text-sm font-bold">↗</span>}
                      {type === 'entrance' && <span style={{ color: config.color }} className="text-sm font-bold">↙</span>}
                      {type === 'bathroom' && <span style={{ color: config.color }} className="text-sm font-bold">WC</span>}
                      {type === 'stairs' && <span style={{ color: config.color }} className="text-sm font-bold">⟰</span>}
                      {type === 'elevator' && <span style={{ color: config.color }} className="text-sm font-bold">⬍</span>}
                      {type === 'wardrobe' && <span style={{ color: config.color }} className="text-sm font-bold">👔</span>}
                      {type === 'smoking' && <span style={{ color: config.color }} className="text-sm font-bold">🚬</span>}
                      {type === 'firstaid' && <span style={{ color: config.color }} className="text-sm font-bold">+</span>}
                      {type === 'info' && <span style={{ color: config.color }} className="text-sm font-bold">i</span>}
                      {type === 'parking' && <span style={{ color: config.color }} className="text-sm font-bold">P</span>}
                      {type === 'foodcourt' && <span style={{ color: config.color }} className="text-sm font-bold">🍴</span>}
                      {type === 'wheelchair' && <span style={{ color: config.color }} className="text-sm font-bold">♿</span>}
                    </div>
                    <span className="text-[10px] text-zinc-400 text-center leading-tight">{config.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader><DialogTitle className="text-white">Seleccionar Plantilla</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            {templates.map(t => (
              <button key={t.id} onClick={() => applyTemplate(t)} className="p-4 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800 hover:border-violet-500/50 text-left">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center"><Layout className="text-[#D4AF37]" size={20} /></div>
                  <div><h4 className="font-bold text-white">{t.name}</h4><p className="text-xs text-zinc-500">{t.default_sections?.length || 0} elementos</p></div>
                </div>
                <p className="text-sm text-zinc-400">{t.description}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Super Productora Upgrade Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Crown className="text-[#D4AF37]" size={24} />
              Función Premium
            </DialogTitle>
          </DialogHeader>
          <UpgradeModalBody onClose={() => setShowUpgradeModal(false)} getPaymentLink={getPaymentLink} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

/**
 * Upgrade modal body with progressive offering:
 *  - First 2 dismissals: standard upgrade flow.
 *  - From dismissal #3 onwards: shows a 7-day free trial CTA to lower the barrier.
 */
const UpgradeModalBody = ({ onClose, getPaymentLink }) => {
  const [dismissals, setDismissals] = useState(0);
  const [startingTrial, setStartingTrial] = useState(false);
  const [trialEligible, setTrialEligible] = useState(true);

  useEffect(() => {
    try {
      const n = parseInt(localStorage.getItem('bravo_upgrade_dismissals') || '0', 10);
      setDismissals(isNaN(n) ? 0 : n);
    } catch (_) { /* localStorage unavailable */ }
    // Check trial eligibility (was it already used?)
    api.get('/producers/me/subscription')
      .then(r => {
        const sub = r.data?.subscription || r.data;
        if (sub?.used_free_trial || sub?.is_trial) setTrialEligible(false);
      })
      .catch(() => {});
  }, []);

  const showTrialOffer = trialEligible && dismissals >= 2;

  const handleDismiss = () => {
    try {
      localStorage.setItem('bravo_upgrade_dismissals', String(dismissals + 1));
    } catch (_) { /* ignore */ }
    onClose();
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const r = await api.post('/producers/me/subscription/start-trial');
      toast.success(r.data?.message || '¡Prueba gratuita activada!');
      try { localStorage.removeItem('bravo_upgrade_dismissals'); } catch (_) { /* ignore */ }
      onClose();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo activar la prueba gratuita');
    } finally {
      setStartingTrial(false);
    }
  };

  const handleSubscribe = () => {
    const link = getPaymentLink();
    if (link) {
      window.open(link, '_blank');
    } else {
      toast.info('Contactá al administrador para suscribirte');
    }
    onClose();
  };

  return (
    <>
      <div className="py-4 space-y-4">
        {showTrialOffer ? (
          <div data-testid="trial-offer" className="rounded-xl p-5 bg-gradient-to-br from-[#D4AF37]/15 via-[#1a1308] to-zinc-900 border border-[#D4AF37]/40 relative overflow-hidden">
            <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-bl-lg bg-[#D4AF37] text-black text-[9px] font-black uppercase tracking-wider">Solo por hoy</div>
            <div className="flex items-center gap-2 mb-2">
              <Gift size={20} className="text-[#D4AF37]" />
              <h4 className="text-white font-bold text-base">7 días gratis</h4>
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed mb-3">
              Probá <span className="text-[#D4AF37] font-bold">Super Productora</span> 7 días sin pagar. Sin tarjeta, sin compromiso. Si te gusta, seguís; si no, vuelve a tu plan Free automáticamente.
            </p>
            <ul className="text-xs text-zinc-400 space-y-1">
              <li className="flex items-center gap-2"><Sparkles size={10} className="text-[#D4AF37]" /> Todos los estilos visuales (9 estilos)</li>
              <li className="flex items-center gap-2"><Sparkles size={10} className="text-[#D4AF37]" /> Hasta 5 venues con fondos premium</li>
              <li className="flex items-center gap-2"><Sparkles size={10} className="text-[#D4AF37]" /> Comisión 10% (vs 15%)</li>
              <li className="flex items-center gap-2"><Sparkles size={10} className="text-[#D4AF37]" /> Email marketing + liquidaciones anticipadas</li>
            </ul>
          </div>
        ) : (
          <>
            <p className="text-zinc-300">
              Los estilos premium como <strong>Neón</strong>, <strong>Holográfico</strong>, <strong>Luxury</strong> y más están disponibles exclusivamente para suscriptores de <span className="text-[#D4AF37] font-bold">Super Productora</span>.
            </p>
            <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium text-white flex items-center gap-2">
                <Crown size={16} className="text-[#D4AF37]" />
                Beneficios Super Productora
              </h4>
              <ul className="text-sm text-zinc-400 space-y-2">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Hasta 5 venues/planos</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Todos los estilos visuales (9 estilos)</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Comisión reducida (10% vs 15%)</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Liquidaciones anticipadas</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" /> Empleos ilimitados y más</li>
              </ul>
            </div>
          </>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={handleDismiss} className="flex-1 border-zinc-700" data-testid="upgrade-dismiss-btn">
          Quizás luego
        </Button>
        {showTrialOffer ? (
          <Button
            onClick={handleStartTrial}
            disabled={startingTrial}
            data-testid="start-trial-btn"
            className="flex-1 bg-gradient-to-r from-[#D4AF37] to-amber-600 hover:from-[#C4A030] hover:to-amber-700 text-black font-bold"
          >
            <Gift size={16} className="mr-2" />
            {startingTrial ? 'Activando…' : 'Probar 7 días gratis'}
          </Button>
        ) : (
          <Button
            onClick={handleSubscribe}
            data-testid="upgrade-subscribe-btn"
            className="flex-1 bg-gradient-to-r from-[#D4AF37] to-amber-600 hover:from-[#C4A030] hover:to-amber-700 text-black font-bold"
          >
            <Crown size={16} className="mr-2" />
            Suscribirme
          </Button>
        )}
      </div>
    </>
  );
};

/**
 * SVG overlay for editing canvas polygon vertices.
 * - Drag a vertex to move it.
 * - Click on an edge segment to insert a new vertex there.
 * - Double-click a vertex to delete it (minimum 3 vertices).
 */
const PolygonOverlay = ({ polygon, onChange, width, height }) => {
  const svgRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  const toLocal = (clientX, clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const px = ((clientX - rect.left) / rect.width) * 100;
    const py = ((clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.max(0, Math.min(100, px)),
      y: Math.max(0, Math.min(100, py)),
    };
  };

  const handleMouseMove = (e) => {
    if (dragIndex === null) return;
    const pt = toLocal(e.clientX, e.clientY);
    const next = polygon.map((p, i) => (i === dragIndex ? pt : p));
    onChange(next);
  };

  const handleMouseUp = () => setDragIndex(null);

  const handleEdgeClick = (i, e) => {
    if (e.target.tagName === 'circle') return; // ignore vertex clicks
    e.stopPropagation();
    const pt = toLocal(e.clientX, e.clientY);
    const next = [...polygon];
    next.splice(i + 1, 0, pt);
    onChange(next);
  };

  const handleVertexDoubleClick = (i, e) => {
    e.stopPropagation();
    if (polygon.length <= 3) {
      toast.error('El polígono necesita al menos 3 vértices');
      return;
    }
    const next = polygon.filter((_, idx) => idx !== i);
    onChange(next);
  };

  const pointsAttr = polygon.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <svg
      ref={svgRef}
      data-testid="polygon-overlay"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="absolute inset-0 pointer-events-auto"
      style={{ width, height, zIndex: 50 }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Filled outline */}
      <polygon
        points={pointsAttr}
        fill="rgba(139,92,246,0.08)"
        stroke="#a78bfa"
        strokeWidth="0.4"
        strokeDasharray="1.5 1"
        vectorEffect="non-scaling-stroke"
        style={{ pointerEvents: 'none' }}
      />
      {/* Edges (clickable to add vertex) */}
      {polygon.map((p, i) => {
        const next = polygon[(i + 1) % polygon.length];
        return (
          <line
            key={`e-${i}`}
            x1={p.x} y1={p.y} x2={next.x} y2={next.y}
            stroke="transparent"
            strokeWidth="3"
            vectorEffect="non-scaling-stroke"
            style={{ cursor: 'copy' }}
            onClick={(e) => handleEdgeClick(i, e)}
          />
        );
      })}
      {/* Vertices */}
      {polygon.map((p, i) => (
        <circle
          key={`v-${i}`}
          data-testid={`polygon-vertex-${i}`}
          cx={p.x} cy={p.y} r="1.2"
          fill={dragIndex === i ? '#fff' : '#a78bfa'}
          stroke="#fff"
          strokeWidth="0.4"
          vectorEffect="non-scaling-stroke"
          style={{ cursor: 'grab' }}
          onMouseDown={(e) => { e.stopPropagation(); setDragIndex(i); }}
          onDoubleClick={(e) => handleVertexDoubleClick(i, e)}
        />
      ))}
    </svg>
  );
};

export default VenueEditor;
